/**
 * Single API client for the PriceQ frontend.
 * All HTTP calls go through here so auth headers, error handling, and
 * base URL stay consistent. Per docs/conventions.md.
 */

import i18n from '@/i18n';
import { API_BASE as BASE } from '@/lib/apiBase';
import { getToken, clearToken, restoreSession, LOGIN_URL } from '@/lib/auth';
import type { AdAttributionParams } from '@/lib/adAttribution';
import type { FacetSelection } from '@/lib/facets';
import * as impersonation from '@/lib/impersonation';
import { onthoudMislukking } from '@/lib/melden/context';
import type {
  MeldingDetail,
  MeldingLijst,
  MeldVrijgave,
  NieuwBericht,
  NieuweMelding,
  Verstuurd,
} from '@/lib/melden/types';
import { toast } from '@/hooks/useToast';
import type { Role } from '@/lib/roles';

/** Per-user notification preference (users.digest_mode). */
export type DigestMode = 'instant' | 'daily' | 'off';
/** Same three the backend accepts (`users.language`, migration 0153) and the
 *  report copy carries. A language the screen offers but the mail cannot
 *  render is a customer whose dashboard and report disagree. */
export type Locale = 'nl' | 'en' | 'de' | 'fr' | 'pl';

export interface NotificationPreference {
  digest_mode: DigestMode;
}

export type SkuStatus = 'active' | 'archived' | 'deleted';

export interface Sku {
  id: string;
  sku: string;
  ean: string;
  name: string;
  /** Slug-string. Mirrors `category_id` during M1/M2 (dual-write); will be
   *  removed in M3 once all readers consume the FK. */
  category: string;
  brand: string | null;
  status: SkuStatus;
  attributes: Record<string, unknown> | null;
  pinned: boolean;
  /** Catalog origin: 'manual' (CSV/hand-added) vs a connector platform. Drives
   *  the 'uit shop' / 'handmatig' badge. */
  source: string;
  sync_status: string;
  /** Jouw eigen prijs in centen — verkoopprijs, of de adviesprijs als die er
   *  niet is. `null` = niet ingevuld, en dan valt er over dit product niets
   *  te vergelijken. Zelfde definitie als het weekrapport. */
  own_price_cents: number | null;
  created_at: string;
  updated_at: string;
}

/** Sentinel for filtering / bulk-moving SKUs that have no category_id.
 *  Backend accepts this both on GET /skus?category=__none__ and as a value
 *  in comma-lists like "gpu,__none__". */
export const UNCATEGORIZED_SLUG = '__uncategorized__';
export const NULL_CATEGORY_QUERY_VALUE = '__none__';

export type CategorySource = 'manual' | 'woocommerce' | 'shopify' | 'magento';

export interface SkuBulkUpdateRequest {
  ids: string[];
  /** Field absent → don't touch category. `null` → move to Ongecategoriseerd.
   *  UUID → set (tenant-validated). */
  category_id?: string | null;
  status?: SkuStatus;
}

export interface SkuBulkUpdateResponse {
  updated_count: number;
}

export interface SkuListResponse {
  items: Sku[];
  total: number;
}

export interface SkuSearchHit {
  id: string;
  sku: string;
  ean: string;
  name: string;
  brand: string | null;
}

// SkuLinkedListing — STUB during v2 rebuild. Frontend SkuDetailPage's
// "linked listings" tab references this type; backend returns empty list
// until Fase 6 wires the new mapping-based shape.
export interface SkuLinkedListing {
  listing_id: string;
  monitored_retailer_id: string;
  retailer_slug: string;
  retailer_display_name: string;
  listing_url: string;
  title: string;
  price_cents: number;
  currency: string;
  availability: string | null;
  scraped_at: string;
}

export interface SkuReferencePrice {
  country: string;
  currency: string;
  msrp_cents: number | null;
  sell_price_cents: number | null;
  cost_cents: number | null;
  min_price_cents: number | null;
  max_price_cents: number | null;
  updated_at: string;
}

export type FloorMode = 'absolute' | 'cost_plus_pct' | 'pct_under_msrp' | 'pct_under_sell';
export type CeilingMode = 'absolute' | 'pct_over_msrp';

export interface PriceSetupUpdate {
  country?: string;
  cost_cents?: number | null;
  msrp_cents?: number | null;
  min_price_cents?: number | null;
  max_price_cents?: number | null;
}

export interface BulkFloorRequest {
  ids: string[];
  country?: string;
  floor_mode?: FloorMode;
  floor_value?: number;
  ceiling_mode?: CeilingMode;
  ceiling_value?: number;
}

export interface BulkFloorResult {
  floor_updated: number;
  ceiling_updated: number;
  total: number;
}

// === Catalogue-wide Trends (redesigned Trends page) ========================
export interface BandPoint {
  bucket_start: string;
  me_avg_cents: number | null;
  market_avg_cents: number | null;
  market_min_avg_cents: number | null;
  market_max_avg_cents: number | null;
  /** Mean of the per-SKU own/market ratio; NOT me_avg / market_avg. */
  index_pct: number | null;
  index_skus_n: number;
}
export interface TrendsKpis {
  market_avg_cents: number | null;
  market_avg_delta_pct: number | null;
  you_vs_market_cents: number | null;
  index_pct: number | null;
  index_skus_n: number;
  own_products_n: number;
  price_changes_n: number;
  retailers_with_changes_n: number;
}
export interface LeaderSegment {
  bucket_start: string;
  retailer_slug: string;
  display_name: string;
  is_self: boolean;
}
export interface PriceLeader {
  segments: LeaderSegment[];
  current_leader_slug: string | null;
  current_leader_name: string | null;
  weeks_you_led: number;
  weeks_total: number;
}
export interface TrendsPortfolioResponse {
  window_days: number;
  kpis: TrendsKpis;
  band: BandPoint[];
  leader: PriceLeader;
}
export interface CategoryRollupRow {
  category_slug: string;
  display_name: string;
  skus_n: number;
  spark_cents: number[];
  net_up_n: number;
  net_down_n: number;
  cheapest_n: number;
  tied_n: number;
  undercut_n: number;
  position_denom_n: number;
  margin_at_stake_cents: number;
  headroom_cents: number;
}
export interface CategoryRollupResponse {
  window_days: number;
  rows: CategoryRollupRow[];
}
export interface TrendMoverRow {
  sku_id: string;
  sku: string;
  name: string;
  brand: string | null;
  retailer_slug: string;
  retailer_display_name: string;
  first_price_cents: number;
  last_price_cents: number;
  delta_cents: number;
  delta_pct: number;
  meaning: 'pressure' | 'room';
  relation: 'below_me' | 'at_me' | 'above_me' | 'unknown';
  my_price_cents: number | null;
  sparkline: number[];
}
export interface TrendMoversResponse {
  window_days: number;
  items: TrendMoverRow[];
}

// === Facet filters (facet-filter sidebar) ==================================
export interface FacetKey {
  /** null for a filter that comes from the shared category and that this
   *  customer never had measured separately — there is no per-customer row
   *  behind it. The rail reads neither field; they stay for the approval
   *  screen (see backend/facets/schemas.py). */
  id: string | null;
  key_slug: string;
  label: string;
  grounding_pct: number | null;
  value_confidence: 'active' | 'rejected' | 'uncertain';
  hits: number | null;
  n: number | null;
  precision_pct: number | null;
  precision_n: number | null;
  sample_values: string[] | null;
  sort_order: number;
  nav_rank: number | null;
  /** 'hoofd' stays open in the rail; 'sub' and 'verfijning' hide behind
   *  "meer filters". Older/un-agent-scored keys come back from the API as
   *  'hoofd' too (see backend/facets/repository.py) — that default is
   *  deliberate: an unrecognised or missing tier must read as visible, not
   *  hidden, or the whole rail goes empty for every un-scored key. */
  tier: 'hoofd' | 'sub' | 'verfijning';
  /** How to draw this filter, measured server-side from the values it holds.
   *  'number' means one range control over value_min..value_max — rendering a
   *  measurement as checkboxes is what turned 151 card lengths into 72 of
   *  them. null = never measured; draw a list. */
  data_type: 'number' | 'enum' | 'bool' | null;
  unit: string | null;
  value_min: number | null;
  value_max: number | null;
}
export interface FacetKeysResponse { threshold_pct: number; keys: FacetKey[]; }
export interface FacetValue { value: string; count: number; }
export interface FacetValuesResponse { key_slug: string; values: FacetValue[]; has_more: boolean; }

// === Facet-proposal approval screen (admin) =================================
// A proposed key carries everything FacetKey does, plus the agent's evidence
// and the decision that was once taken on it. `evidence` and `decided_reason`
// only ever come back from the admin /proposals endpoint — the tenant-facing
// /keys endpoint (approved-only) never exposes either. Read-only since fase 4:
// deciding happens once per shared category (adminOps.categoryNodes), not per
// customer, so nothing writes `status` or `decided_reason` any more.
export interface FacetProposalKey {
  id: string;
  key_slug: string;
  label: string;
  grounding_pct: number;
  hits: number | null;
  n: number | null;
  precision_pct: number | null;
  precision_n: number | null;
  sample_values: string[] | null;
  sort_order: number;
  nav_rank: number | null;
  status: 'proposed' | 'approved' | 'rejected';
  decided_reason: string | null;
  tier: 'hoofd' | 'sub' | 'verfijning';
  evidence: string | null;
  data_type: 'number' | 'enum' | 'bool' | null;
  unit: string | null;
  value_min: number | null;
  value_max: number | null;
  /** Catalogue measurement (backend/facets/measure.py), not a model claim.
   *  `null` means "never measured" — never coerce to 0, they mean different
   *  things: 0 coverage is a real (damning) result, null is "unknown". */
  coverage_pct: number | null;
  distinct_values: number | null;
  top_share_pct: number | null;
  /** key_slug of another proposed key whose value already fully determines
   *  this one's (e.g. GPU chip always implies its VRAM) — null if none. */
  determined_by: string | null;
  /** Axes the agent itself flagged as uncertain this research round — what
   *  it doesn't know and why (backend/agents/category_filters.md step 3).
   *  The agent has no way to look anything up in a run; this is how doubt it
   *  can't settle itself reaches the operator instead of getting guessed
   *  away. Same list on every key from the same round, like panel_urls. */
  open_questions: { label: string; question: string }[];
  /** The value vocabulary the filling and the gate run on
   *  (backend/agents/category_filters/vocabulary.py). Either an enumerable
   *  list — each value with the aliases that appear in a product name, at
   *  most one marked `default` for "when nothing else matches" — or the
   *  string 'open' when the set cannot be listed (a size, a model name that
   *  changes every generation), in which case `pattern` does the work.
   *  `null` on keys proposed before this existed. Shown before approval:
   *  that is the only moment an error in this list is still cheap. */
  values: { value: string; aliases: string[]; default?: boolean }[] | 'open' | null;
  /** Regex with exactly one capture group; only set when values === 'open'. */
  pattern: string | null;
}
export interface FacetProposalsResponse { keys: FacetProposalKey[]; }
export interface FacetResearchResponse { job_id: string; }

/** The measuring stick (backend/facets/health.py). Deliberately un-judged:
 *  no "suspicious" flag, no score. Duplicate spellings and junk values are
 *  returned exactly as they sit in the bar, so a human sees them instead of
 *  having to trust that something upstream caught them. */
export interface FacetHealthKey {
  key_slug: string;
  label: string;
  tier: 'hoofd' | 'sub' | 'verfijning';
  filled: number;
  total: number;
  coverage_pct: number;
  distinct_values: number;
  /** The value list was truncated — the tail (where the junk lives) is not
   *  fully shown. Surfaced in the UI rather than swallowed. */
  has_more_values: boolean;
  values: { value: string; count: number }[];
}
export interface FacetHealthResponse {
  total_products: number;
  hoofd_keys: number;
  /** Products with no value on ANY main filter — unreachable in one click. */
  orphan_count: number;
  orphans: { sku: string; name: string }[];
  keys: FacetHealthKey[];
}

/** What the filter bar would look like today vs. after fase 3 flips the
 *  read path to the shared library (backend/admin/library_compare.py).
 *  `node_id: null` means the category isn't linked yet — the read path keeps
 *  falling back to the old per-customer table, so nothing changes. */
export interface LibraryCompareResponse {
  node_id: string | null;
  node_name: string | null;
  link_source: string | null;
  today: string[];
  after: string[];
  gained: string[];
  lost: string[];
}

export interface DashboardKpiTile {
  value: number;
  delta_vs_prev: number;
  delta_label: string;
  delta_is_bad: boolean;
  sparkline: number[];
}

export interface DashboardGreeting {
  tenant_name: string;
  last_login_ts: string | null;
  last_login_ip: string | null;
}

export interface DashboardLastScrape {
  ts: string | null;
  retailers_scraped: number;
  retailers_total: number;
  failed: number;
}

export interface DashboardAiCost {
  spent_cents: number;
  budget_cents: number;
  period_days: number;
}

export interface DashboardMatchQueue {
  total: number;
  critical: number;
  accept_rate_30d: number | null;
}

export interface DashboardSummary {
  greeting: DashboardGreeting;
  kpis: {
    open_alerts: DashboardKpiTile;
    critical: DashboardKpiTile;
    listings_tracked: DashboardKpiTile;
    avg_discount_pct: DashboardKpiTile;
  };
  last_scrape: DashboardLastScrape;
  ai_cost: DashboardAiCost;
  match_queue: DashboardMatchQueue;
}

// === Dashboard v2 (Plan 6/7) ===============================================
// Schemas from `.ai/redesign/dashboard/contract.md` — keep in lock-step with
// backend/dashboard/schemas.py.

export interface CategoryTag {
  slug: string;
  display_name: string;
}

export interface OwnPositionDistribution {
  cheapest_pct: number | null;
  tied_pct: number | null;
  undercut_pct: number | null;
  /** Exact count behind undercut_pct — SKUs with ≥1 competitor below my price. */
  undercut_n: number;
  denominator_n: number;
  skus_without_own_listing_n: number;
}

export interface StockBlock {
  own_in_stock_pct: number | null;
  own_known_n: number;
  own_unknown_n: number;
  competitor_in_stock_pct: number | null;
  competitor_known_n: number;
  competitor_unknown_n: number;
  own_oos_competitor_in_pct: number | null;
  own_oos_competitor_known_n: number;
  /** Exact count behind own_oos_competitor_in_pct — I'm OOS, competitor in stock. */
  own_oos_competitor_in_n: number;
}

export interface FreshnessBlock {
  pct_fresh_24h: number | null;
  observations_n: number;
  stalest_scraped_at: string | null;
  freshness_window_hours: number;
}

// Single-category response (no `?group_by=category`).
export interface PortfolioSummaryResponse {
  skus_total: number;
  skus_with_competitors: number;
  skus_with_own_listing: number;
  has_self_retailer: boolean;
  own_position_distribution: OwnPositionDistribution;
  avg_gap_to_cheapest_pct: number | null;
  /** Σ(my price − cheapest competitor) cents over undercut SKUs (is_self-anchored, ≥0). */
  margin_at_stake_cents: number;
  stock: StockBlock;
  freshness: FreshnessBlock;
}

// Per-category overview row (?group_by=category)
export interface CategoryPositionSummary {
  cheapest_pct: number | null;
  tied_pct: number | null;
  undercut_pct: number | null;
  denominator_n: number;
}

export interface PerCategoryEntry {
  category_slug: string;
  display_name: string;
  skus_n: number;
  own_position_distribution: CategoryPositionSummary;
  attention_score: number;
  undercut_n: number;
  stock_gap_n: number;
  critical_n: number;
}

export interface PortfolioOverviewTotals {
  skus_total: number;
  skus_attention_n: number;
  critical_n: number;
  has_self_retailer: boolean;
  freshness: FreshnessBlock;
}

export interface PortfolioByCategoryResponse {
  totals: PortfolioOverviewTotals;
  by_category: PerCategoryEntry[];
}

// === Outliers ==============================================================

export interface HardestUndercutEntry {
  sku_id: string;
  sku: string;
  name: string;
  category: CategoryTag;
  my_price_cents: number;
  cheapest_competitor_cents: number;
  gap_pct: number;
  cheapest_retailer_slug: string;
  scraped_at: string;
}

export interface ByFarCheapestEntry {
  sku_id: string;
  sku: string;
  name: string;
  category: CategoryTag;
  my_price_cents: number;
  next_competitor_cents: number;
  next_competitor_slug: string;
  margin_pct: number;
  scraped_at: string;
}

export interface StockGapImEmptyEntry {
  sku_id: string;
  sku: string;
  name: string;
  category: CategoryTag;
  competitor_slug: string;
  competitor_price_cents: number;
  scraped_at: string;
}

export interface StockGapCompetitorEmptyEntry {
  sku_id: string;
  sku: string;
  name: string;
  category: CategoryTag;
  competitor_slug: string;
  my_price_cents: number;
  scraped_at: string;
}

export interface OutliersResponse {
  hardest_undercut: HardestUndercutEntry[];
  by_far_cheapest: ByFarCheapestEntry[];
  stock_gap_im_empty: StockGapImEmptyEntry[];
  stock_gap_competitor_empty: StockGapCompetitorEmptyEntry[];
  limit: number;
  has_self_retailer: boolean;
}

// MatchQueueStats — DEPRECATED (v1 matching pipeline removed). Kept as type
// alias because dashboard.summary still ships the field; v2 replaces with
// MappingReviewStats. Frontend treats both null + zero-stats as "no data".
export interface MatchQueueStats {
  total: number;
  critical: number;
  accept_rate_30d: number | null;
}

export interface AdminSpendByWorkload {
  workload: string;
  cost_cents: number;
  call_count: number;
}

export interface AdminSpendByTenant {
  tenant_id: string;
  slug: string;
  cost_cents: number;
  call_count: number;
}

export interface AdminSpendResponse {
  period_days: number;
  cost_cents: number;
  call_count: number;
  by_workload: AdminSpendByWorkload[];
  by_tenant: AdminSpendByTenant[];
}

export interface SkuCreate {
  sku: string;
  ean: string;
  name: string;
  category: string;
  brand?: string | null;
  attributes?: Record<string, unknown> | null;
}

export interface SkuCsvImportResponse {
  created_count: number;
  updated_count: number;
  failed_count: number;
  /** `code` is the translatable reason; `reason` is the server's English fallback. */
  failures: Array<{ row: number; code?: string; reason: string }>;
  /** Set when the linking pipeline was enqueued; poll bulkRunStatus with it. */
  bulk_run_id: string | null;
  /** New products left out because the plan was full (#646). Optional: an
   *  older backend does not send it. */
  skipped_count?: number;
  /** The plan's product ceiling; null when the file added nothing new. */
  sku_limit?: number | null;
}

export type SkuSchemaField = 'sku' | 'ean' | 'name' | 'category' | 'brand' | 'msrp';

export interface SkuColumnMapping {
  csv_column: string;
  schema_field: SkuSchemaField | null;
  confidence: number;
  reason: 'exact' | 'synonym' | 'ai' | 'unmapped' | 'user';
}

export interface SkuSampleRow {
  row_number: number;
  raw: Record<string, string>;
  normalized: Record<string, string>;
  issues: string[];
}

export interface SkuImportPreview {
  upload_token: string;
  mappings: SkuColumnMapping[];
  samples: SkuSampleRow[];
  total_rows: number;
  missing_required_fields: string[];
  ai_used: boolean;
  /** Article codes that occur more than once, and the rows they span. */
  duplicate_sku_values: number;
  duplicate_sku_rows: number;
  /** EANs that occur more than once, and the rows they span. */
  duplicate_ean_values: number;
  duplicate_ean_rows: number;
  /** Rows whose EAN is missing or malformed — refused on import. */
  invalid_ean_rows: number;
  /** The offending values themselves, capped server-side. */
  duplicate_sku_examples: SkuRepeatedValue[];
  duplicate_ean_examples: SkuRepeatedValue[];
  invalid_ean_examples: SkuInvalidValueRow[];
}

export interface SkuImportIssueReport {
  sent_to: string;
  issue_count: number;
  total_rows: number;
}

export interface SkuRepeatedValue {
  value: string;
  row_numbers: number[];
}

export interface SkuInvalidValueRow {
  row_number: number;
  value: string;
}

class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly body?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The machine-readable `code` on a structured refusal, or null.
 *
 * The house convention for a refusal this API means a caller to ACT on is
 * `{"detail": {"code": ..., "message": ..., ...}}` -- `read_only`, the two
 * webhook 409s, the webhook 503. Reading it was written out three times (the
 * 403 branch below, isLookAlongRefusal, and a private copy on the webhooks
 * tab), and the next code-carrying refusal would have made four. One rule,
 * one place: `detail` is a string on most of this API and an object here, so
 * neither shape may be assumed.
 */
export function apiErrorCode(err: unknown): string | null {
  const code = apiErrorDetail(err)?.code;
  return typeof code === 'string' ? code : null;
}

/**
 * The whole structured refusal body, for the few refusals that carry more than
 * a code. The billing `409 amount_changed` is why this exists: it ships the
 * figure the operator approved AND the one the provider now names, and the
 * screen has to show the second without re-deriving how `detail` is shaped.
 */
export function apiErrorDetail(err: unknown): Record<string, unknown> | null {
  if (!(err instanceof ApiError)) return null;
  const detail = (err.body as { detail?: unknown } | null | undefined)?.detail;
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return null;
  return detail as Record<string, unknown>;
}

// Pull the user-facing message out of an error, preferring FastAPI's
// `{ detail: string }` body over the generic "POST /path failed: 400".
// 422 validation errors come as `{ detail: [{loc, msg, type}, ...] }`.
export function formatApiError(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const detail = (err.body as { detail?: unknown }).detail;
    // Three different 403s can reach an operator, and only one of them is a
    // permissions problem:
    //   1. the read-only fence (backend/auth/dependencies.py) refusing a
    //      change — `{"detail": {"code": "read_only", ...}}`. The backend's own
    //      sentence is fine, but it is the backend's; ours is translated and
    //      says the same thing in the words the band uses.
    //   2. any OTHER 403 while looking along. The look-along token carries
    //      role=viewer, and four GETs refuse a viewer -- /team/members,
    //      /audit-log, /exports/full and /api-keys. That is not an error, it is
    //      a screen this session may not open, and it must not read as a bug.
    //   3. a 403 outside a look-along: untouched, whatever it was before.
    if (err.status === 403) {
      if (apiErrorCode(err) === 'read_only') return i18n.t('admin.lookAlong.readOnly');
      if (impersonation.get()) return i18n.t('admin.lookAlong.screenHidden');
    }
    // Cases 1 and 2 above are a policy, not a fault. isLookAlongRefusal is the
    // same test, so a caller that has to FRAME the message (a toast title, say)
    // asks one function instead of re-deriving the 403 rules.
    if (typeof detail === 'string' && detail.length > 0) return detail;
    // Structured refusals (plan limits, missing plan features) send an object
    // whose `message` is already written for the customer. Handled here rather
    // than per screen, so every caller of formatApiError shows the real reason
    // instead of "POST /api/v1/skus failed: 402".
    if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
      // The backend writes its plan message in Dutch only. Where a translation
      // exists (common.planLimit.<resource>), the customer reads it in his own
      // language; otherwise the backend sentence below still beats a status code.
      const { resource, limit } = detail as { resource?: unknown; limit?: unknown };
      const planKey = `common.planLimit.${String(resource)}`;
      if (apiErrorCode(err) === 'plan_limit_reached' && i18n.exists(planKey)) {
        return i18n.t(planKey, { limit });
      }
      const message = (detail as { message?: unknown }).message;
      if (typeof message === 'string' && message.length > 0) return message;
    }
    if (Array.isArray(detail)) {
      const msgs = detail
        .map((d) => (d && typeof d === 'object' && 'msg' in d ? String((d as { msg: unknown }).msg) : null))
        .filter((m): m is string => !!m);
      if (msgs.length > 0) return msgs.join('; ');
    }
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/**
 * Is this refusal the look-along, rather than something going wrong?
 *
 * The sentence formatApiError produces is already right; what a caller cannot
 * derive from it is whether to present it as a fault. A toast titled "Er ging
 * iets mis" over a correct read-only refusal sends an operator hunting a bug
 * that is a policy — the defect this branch has removed four times.
 */
export function isLookAlongRefusal(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 403) return false;
  return apiErrorCode(err) === 'read_only' || !!impersonation.get();
}

// Tenant_id now travels inside the JWT (claim), not a header. The frontend only
// attaches the bearer token; the backend derives tenant_id from it.
function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// The language lives in the browser (localStorage `priceq.lang`), so anything
// the server writes for the customer to read -- the data export's filenames,
// column headers and README -- is in the wrong language unless we say which one
// on the request itself.
function baseHeaders(): Record<string, string> {
  const lang = i18n.language?.split('-')[0];
  return { ...authHeaders(), ...(lang ? { 'Accept-Language': lang } : {}) };
}

// On a 401, only bounce to /login when a token WAS present (an expired/invalid
// session). NEVER hard-redirect for:
//   - auth endpoints (login itself returns 401 on bad credentials), or
//   - realtime/ticket (the always-on SSE poller), or
//   - token-less calls (unauthenticated state is handled by <ProtectedRoute>).
// Without this gate, a token-less realtime-ticket 401 would window.location to
// /login on a loop and interrupt the login form mid-submit.
function handleUnauthorized(path: string): void {
  if (path.startsWith('/api/v1/auth/')) return;
  // RULING 4. A look-along runs for thirty minutes and then every request 401s.
  // Sending the operator to the login screen over that would end their OWN
  // session — they were never signed out, a support view expired. And falling
  // back to their own session silently is worse: they would go on reading what
  // they believe are the customer's figures while looking at their own.
  // So: end the look-along, say so, stay on this screen.
  if (impersonation.get()) {
    impersonation.clear();
    // And this tab keeps no credential at all. Other requests are usually
    // still in flight when the first one 401s, and each decides its own
    // refresh question AFTER the line above has removed the look-along -- at
    // which point `getToken()` is the only thing standing between them and a
    // refresh-and-retry as the operator. Measured as a real race.
    clearToken();
    toast.error(i18n.t('admin.lookAlong.expired'));
    return;
  }
  // Realtime is exempt from the login bounce, not from the line above: the
  // ticket POST is the one write a look-along may make, so at minute 31 it is
  // usually the FIRST thing to 401. Returning here first meant the stream
  // reconnect-looped in silence until some other request happened to fail.
  if (path.startsWith('/api/v1/realtime/')) return;
  if (!getToken()) return;
  clearToken();
  if (typeof window !== 'undefined') window.location.href = LOGIN_URL;
}

// 402 means: your subscription lapsed, but you can still pay. The backend
// lets a suspended customer reach only the billing endpoints, so every other
// call lands here. Sending them to the subscription screen is the whole point
// of 402 existing rather than 403.
//
// Not every 402 is that. A plan limit ("your plan allows 3 shops") is also a
// 402, from a customer whose subscription is fine; sending him to a screen that
// says it lapsed hides the real reason. That one stays put, so the screen that
// asked can show the message. Same for a feature the plan does not include
// (`plan_feature_missing`): it bounced a trial customer to "expired" (#657).
const PLAN_REFUSALS = new Set(['plan_limit_reached', 'plan_feature_missing']);

function isPlanRefusal(payload: unknown): boolean {
  return PLAN_REFUSALS.has(apiErrorCode(new ApiError(402, '', payload)) ?? '');
}

function handlePaymentRequired(path: string): void {
  if (path.startsWith('/api/v1/billing/')) return;
  if (typeof window === 'undefined') return;
  // Real route: /settings?s=subscription (SettingsPage.tsx / sections.ts).
  // '/instellingen/abonnement' does not exist -- it hits the catch-all and
  // renders a blank <main>. Checking pathname only (not the query string) is
  // what stops this from looping: AppShell's own strict queries (tenant,
  // review-queue, setup) also 402 for a suspended tenant on every route,
  // including this one, and each would re-trigger this handler otherwise.
  if (window.location.pathname.includes('/settings')) return;
  // Through the Vite base ('/app/' in production, '/' in dev), for the same
  // reason LOGIN_URL is: a bare '/settings' leaves the app entirely now that
  // the marketing site owns '/'. Router-internal navigation needs no prefix;
  // a hard redirect does.
  window.location.href = `${import.meta.env.BASE_URL}settings?s=subscription&verlopen=1`;
}

// A 401 is not automatically the end of the session. The access token is
// short-lived and lives in memory now, so the ordinary case is "it expired
// while the tab was open" — and the refresh cookie can fix that without
// anybody retyping a password. Only when the refresh also fails is the session
// really over, and then handleUnauthorized bounces to the login screen.
//
// Single-flight: a dashboard fires a dozen queries at once and they would all
// 401 together. One refresh, everyone waits for it, everyone retries. Twelve
// parallel refreshes would rotate the cookie twelve times, and rotation with
// reuse detection reads the second one as a replay — the session would be
// revoked by its own recovery.
let refreshInFlight: Promise<boolean> | null = null;

function refreshOnce(): Promise<boolean> {
  if (refreshInFlight === null) {
    refreshInFlight = restoreSession().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** True when this 401 is worth one refresh-and-retry. */
function mayRetryAfterRefresh(path: string, status: number, attempted: boolean): boolean {
  if (status !== 401 || attempted) return false;
  // A look-along never refreshes. This is the second of two guards and that is
  // deliberate, against this branch's own rule about redundant guards: they
  // stop different failures. `restoreSession`'s guard stops the refresh from
  // HAPPENING; this one stops the decision being taken on a credential that is
  // not this tab's to spend. Deleting this line alone is unobservable today --
  // `restoreSession` still refuses -- so it has no test of its own, and saying
  // so is more honest than a test that cannot go red.
  if (impersonation.get()) return false;
  // The auth routes ARE the refresh path (and /login legitimately 401s on bad
  // credentials); realtime/ticket runs token-less by design.
  if (path.startsWith('/api/v1/auth/') || path.startsWith('/api/v1/realtime/')) return false;
  return !!getToken();
}

async function request<T>(
  method: string, path: string, body?: unknown, attempted = false,
): Promise<T> {
  const headers: Record<string, string> = { ...baseHeaders() };
  // credentials: the refresh cookie is scoped to /api/v1/auth, so it rides
  // along on nothing else — but login and refresh both need it to be stored
  // and sent, and that only happens when the request asks for credentials.
  let init: RequestInit = { method, headers, credentials: 'include' };
  if (body !== undefined && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
    init = { ...init, body: JSON.stringify(body) };
  } else if (body instanceof FormData) {
    init = { ...init, body };
  }
  const response = await fetch(`${BASE}${path}`, init);
  if (!response.ok) {
    if (mayRetryAfterRefresh(path, response.status, attempted) && (await refreshOnce())) {
      return request<T>(method, path, body, true);
    }
    if (response.status === 401) handleUnauthorized(path);
    // P6 (klant-feedback-lus): the report button sends the last five of these.
    // Recording is a side effect: it must never break the request path.
    try {
      onthoudMislukking(method, path, response.status, response.headers?.get?.('X-Request-ID') ?? null);
    } catch { /* ignore */ }
    const payload = await readErrorPayload(response);
    if (response.status === 402 && !isPlanRefusal(payload)) handlePaymentRequired(path);
    throw new ApiError(response.status, `${method} ${path} failed: ${response.status}`, payload);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function requestBlob(method: string, path: string, attempted = false): Promise<Blob> {
  const headers: Record<string, string> = { ...baseHeaders() };
  const response = await fetch(`${BASE}${path}`, { method, headers, credentials: 'include' });
  if (!response.ok) {
    if (mayRetryAfterRefresh(path, response.status, attempted) && (await refreshOnce())) {
      return requestBlob(method, path, true);
    }
    if (response.status === 401) handleUnauthorized(path);
    const payload = await readErrorPayload(response);
    if (response.status === 402 && !isPlanRefusal(payload)) handlePaymentRequired(path);
    throw new ApiError(response.status, `${method} ${path} failed: ${response.status}`, payload);
  }
  return response.blob();
}

async function readErrorPayload(response: Response): Promise<unknown> {
  // Clone so a JSON-parse failure doesn't burn the body stream — without
  // this the fallback .text() throws "stream already read" and masks the
  // real error from the user.
  const clone = response.clone();
  try {
    return await response.json();
  } catch {
    try {
      return await clone.text();
    } catch {
      return null;
    }
  }
}

// === Onboarding wizard types (Phase 3c-2) =====================================

export interface RetailerPreflight {
  /** Het adres is te lezen als een winkeldomein. */
  ok: boolean;
  domain: string | null;
  slug: string | null;
  country: string | null;
  /** 'tld' = uit het achtervoegsel (.de), 'home' = landneutraal (.com) dus jouw
   *  land, 'language' = de site geeft zelf een andere taal/regio op. */
  country_source: 'tld' | 'home' | 'language' | null;
  allowed: boolean;
  blocked_reason:
    | 'bad_url'
    | 'other_country'
    | 'other_country_language'
    | 'already_tracked'
    | 'marketplace_not_supported'
    | null;
  /** `false` betekent alleen dat een kale aanvraag niet langs de
   *  bot-beveiliging kwam — dat blokkeert het toevoegen niet. */
  reachable: boolean;
  site_name: string | null;
}

export interface OnboardRetailerRequest {
  retailer_slug: string;
  base_url: string;
  // ISO 3166-1 alpha-2; backend validates. Widened from the old NL/DE/ES/SE
  // union so the Bronnen add-retailer modal can offer NL/DE/BE/PL/FR.
  country_code: string;
}

export interface OnboardRetailerResponse {
  job_id: string;
}

export type JobEventKind = 'started' | 'done' | 'error' | 'short_circuit';

export interface JobEvent {
  seq: number;
  ts: string;
  stage: string;
  kind: JobEventKind;
  payload: Record<string, unknown>;
}

export interface JobEventsResponse {
  job_id: string;
  events: JobEvent[];
  last_seq: number;
  terminal: boolean;
}

export type ScrapeProvider = 'mock' | 'scrape_do' | 'bright_data' | 'direct' | 'dataimpulse';
export type ScrapeRunStatus = 'running' | 'success' | 'failed' | 'blocked' | 'aborted';

export interface RetailerStatusOnboarding {
  phase:
    | 'pending'
    | 'running'
    | 'auto_live'
    | 'pending_review'
    | 'unsupported'
    | 'degraded'
    | 'failed'
    | 'needs_catalog';
  is_terminal: boolean;
  confidence: number | null;
  current_stage?: string | null;
}

export interface RetailerStatusDiscovery {
  phase: 'not_started' | 'running' | 'completed' | 'failed';
  is_terminal: boolean;
  discovered_count: number;
  last_run_at: string | null;
}

export interface RetailerStatusMapping {
  phase: 'idle' | 'running' | 'completed';
  is_terminal: boolean;
  active_count: number;
  mapped_count: number;
  needs_review_count: number;
  abandoned_count: number;
  total: number;
  // Active SKUs with no mapping_jobs row yet. total + pending_count = catalogue
  // size, which the coverage figure divides by.
  pending_count: number;
  // What a mapping run would pick up right now, including pairs already tried
  // that are due for another attempt. Drives the "Map nu" button.
  mappable_count: number;
  last_run_at: string | null;
}

export interface RetailerStatus {
  onboarding: RetailerStatusOnboarding;
  discovery: RetailerStatusDiscovery;
  mapping: RetailerStatusMapping;
}

export interface RetailerHealth {
  /** `waiting` = nog nooit een prijs binnengekregen. Geen storing, maar ook
   *  geen resultaat — en dus geen "gezond". */
  status: 'healthy' | 'waiting' | 'delayed' | 'action_needed';
  reason: 'blocked_bot' | 'geo_locked' | 'layout_changed' | 'rate_limited' | 'stale' | null;
  fault: 'auto_retry' | 'operator' | null;
  fix_action: 'escalate' | 'remap' | null;
  last_success_at: string | null;
  geo_code: string | null;
  escalated: boolean;
}
export interface HealthDay {
  date: string;
  status: 'ok' | 'warn' | 'bad' | 'unknown';
}
export interface EscalateResponse {
  escalated: boolean;
  geo_code: string | null;
  rescrape_queued: boolean;
  scrape_log_id: string | null;
}

export interface MonitoredRetailer {
  id: string;
  retailer_slug: string;
  display_name: string;
  country: string;
  active: boolean;
  scrape_provider: ScrapeProvider;
  frequency_per_day: number;
  sitemap_url: string | null;
  product_url_regex: string | null; // legacy, kept for one deploy
  product_url_patterns: string[];
  sku_id_capture_group: number;
  state: string;
  consecutive_failures: number;
  // True iff this retailer is the tenant's own webshop (e-tailer dashboard
  // anchor). At most one per tenant — toggling true on another row unsets
  // the previous one on the server.
  is_self: boolean;
  last_discovery_at: string | null;
  created_at: string;
  updated_at: string;
  // Optional — list/detail endpoints populate this; POST/PATCH leave null.
  // UI uses it to render terminal-vs-progress states (#27, #29).
  status?: RetailerStatus | null;
  // Health rollup (Bronnen page) — list/detail populate; POST/PATCH leave null.
  health?: RetailerHealth | null;
}

export interface MonitoredRetailerListResponse {
  items: MonitoredRetailer[];
  total: number;
}

export interface MonitoredRetailerCreate {
  retailer_slug: string;
  display_name: string;
  country: string;
  scrape_provider?: ScrapeProvider;
  frequency_per_day?: number;
  sitemap_url?: string | null;
  product_url_patterns?: string[];
  product_url_regex?: string | null;
  sku_id_capture_group?: number;
}

export interface MonitoredRetailerUpdate {
  display_name?: string | null;
  country?: string | null;
  active?: boolean | null;
  scrape_provider?: ScrapeProvider | null;
  frequency_per_day?: number | null;
  sitemap_url?: string | null;
  product_url_patterns?: string[] | null;
  product_url_regex?: string | null;
  sku_id_capture_group?: number | null;
  is_self?: boolean | null;
}

export interface PatternSuggestion {
  regex: string;
  match_count: number;
  sample_match: string;
  sku_id_capture_group: number;
}

export interface CategoryCluster {
  label: string;
  path_segment: string;
  match_count: number;
  sample_urls: string[];
  detected_brands: string[];
}

export type ScrapePhase =
  | 'queued'
  | 'running'
  | 'fetching'
  | 'validating'
  | 'extracting'
  | 'parsing'
  | 'matching'
  | 'persisting'
  | 'done'
  | 'failed';

export interface ScrapeLog {
  id: string;
  monitored_retailer_id: string;
  status: ScrapeRunStatus;
  started_at: string;
  ended_at: string | null;
  listings_count: number | null;
  credits_used: number | null;
  provider: ScrapeProvider;
  failure_reason: string | null;
  triggered_by: string | null;
  progress_pct: number | null;
  progress_phase: ScrapePhase | null;
}

export interface RealtimeTicketResponse {
  ticket: string;
  expires_in_seconds: number;
}

// === Webshop integrations (Phase 2 — /api/v1/integrations) =================

export type IntegrationPlatform = 'shopify' | 'woocommerce' | 'magento';
export type IntegrationState =
  | 'active'
  | 'degraded'
  | 'scope_insufficient'
  | 'expired_or_revoked'
  | 'disconnected';

export interface IntegrationStatusItem {
  id: string;
  platform: IntegrationPlatform;
  shop_handle: string;
  state: IntegrationState;
  updated_at: string;
}

export interface IntegrationStatusResponse {
  items: IntegrationStatusItem[];
}

// shopify/woocommerce → authorize_url (browser navigates there).
// magento → instructions text block, no authorize_url.
export interface IntegrationConnectResponse {
  authorize_url?: string | null;
  instructions?: string | null;
}

export interface IntegrationManualResponse {
  platform: IntegrationPlatform;
  shop_handle: string;
  state: IntegrationState;
}

// --- Layer-2 catalog selection (opt-in import) ---------------------------- //

export interface CatalogPreviewProduct {
  external_id: string;
  external_parent_id: string | null;
  title: string;
  sku: string | null;
  ean: string | null;
  brand: string | null;
  price_cents: number | null;
  has_cost: boolean;
  category_external_ids: string[];
}

export interface CatalogPreviewCategory {
  external_id: string;
  name: string;
}

export interface CatalogPreviewResponse {
  products: CatalogPreviewProduct[];
  categories: CatalogPreviewCategory[];
  brands: string[];
  currency: string;
  limit: number;
  current_count: number;
  remaining: number;
  truncated: boolean;
  ean_coverage: number;
  cost_coverage: number;
  cost_permission_warning: boolean;
}

export interface CatalogPreviewRequest {
  credential_id: string;
  filter_definition?: Record<string, unknown>;
  store_code?: string | null;
  ean_attribute_code?: string | null;
}

export interface CatalogConfirmRequest {
  credential_id: string;
  filter_definition?: Record<string, unknown>;
  selected_external_ids?: string[] | null;
  store_code?: string | null;
  ean_attribute_code?: string | null;
}

export interface CatalogConfirmResponse {
  scope_id: string;
  selected_count: number;
  monitored_after: number;
  limit: number;
}

// --- Layer-3 pricing (approval queue + push + trust-first) ----------------- #

export type SuggestionStatus =
  | 'pending' | 'approved' | 'rejected' | 'pushed' | 'failed' | 'blocked';

export interface ExplanationStep {
  stage: string;
  [k: string]: unknown;
}

export interface PriceSuggestion {
  id: string;
  sku_id: string;
  sku: string | null;
  title: string;
  brand: string | null;
  platform: string;
  country: string;
  current_price_cents: number | null;
  suggested_price_cents: number | null;
  match_confidence: number | null;
  status: SuggestionStatus;
  rule_name: string | null;
  explanation_trace: ExplanationStep[];
  updated_at: string;
  // Design-parity (Fase 0): margin display + own-rule badge.
  cost_cents: number | null;
  current_margin_pct: number | null;
  suggested_margin_pct: number | null;
  min_price_cents: number | null;
  is_own: boolean;
}

export interface GenerateResult {
  suggestions: number;
  blocked: number;
  skipped: number;
  health_events: number;
  /** Products already priced right — the move was too small to be worth a
   *  click. Without this, "8 proposals out of 214 products" reads like a
   *  failure. */
  unchanged: number;
}

/** What a rule WOULD do, without applying it. */
export interface RulePreviewRequest {
  rule_type: string;
  params: Record<string, unknown>;
  scope: Record<string, unknown>;
  country: string;
}

export interface RulePreview {
  in_scope: number;
  would_change: number;
  down: number;
  up: number;
  blocked: number;
  unchanged: number;
  no_signal: number;
  /** Sum of the differences; negative = prices come down. */
  net_move_cents: number;
}

/** The three rules that apply to every suggestion, whatever strategy made it. */
export interface PricingPolicy {
  /** Round every price to this ending (0, 50, 95, 99). Null = do not round. */
  price_ending: number | null;
  /** Ignore a competitor price below this share of the median. 0 = off. */
  outlier_floor_pct: number;
  min_change_pct: number;
  min_change_cents: number;
}

export interface PushLogEntry {
  id: string;
  sku_id: string | null;
  external_id: string | null;
  platform: string | null;
  old_price_cents: number | null;
  new_price_cents: number | null;
  verified_price_cents: number | null;
  actor: string;
  result: string | null;
  pushed_at: string;
  /** The round this row belongs to; null on rows from before rounds existed. */
  batch_id: string | null;
  /** The proposal this row is about; what "opnieuw proberen" acts on. */
  suggestion_id: string | null;
}

/** Why a manual round needs its count typed: the same measure the brake uses. */
export type PushConfirmReason = 'too_many_changes' | 'too_many_at_floor' | 'too_many_lowered';

/** What a manual round would send right now (GET /pricing/push/preview). */
export interface PushPreview {
  count: number;
  catalogue: number;
  share_pct: number;
  confirm_required: boolean;
  reason: PushConfirmReason | null;
  /** The last round stopped with work left; "Verder versturen" sends the rest. */
  interrupted: { batch_id: string; started_at: string; remaining: number } | null;
  /** The send dialog's tiles: how many go down, up, and were looked at. */
  lowering: number;
  raising: number;
  checked: number;
  /** The biggest step in percent, signed. */
  largest_step_pct: number | null;
  /** The job writes in batches of this size; above it a round runs in the background. */
  batch_size: number;
  /** The brake's limit for the reason it gives, when it would trip. */
  limit_pct: number | null;
  /** Every row of a small round; the biggest checked steps of a big one. */
  examples: { name: string; current_price_cents: number | null; suggested_price_cents: number }[];
}

export interface PushOptions {
  /** The count the human typed, for a round big enough to need it. */
  confirm_count?: number;
  resume?: boolean;
}

export type DataHealthKind = 'dead_url' | 'match_collapse' | 'scrape_fail' | 'stale';

export interface DataHealthEvent {
  id: string;
  source: string;
  kind: DataHealthKind;
  detail: Record<string, unknown>;
  sku_id: string | null;
  at: string;
}

export interface PricingRule {
  id: string;
  name: string;
  rule_type: string;
  params: Record<string, unknown>;
  scope: Record<string, unknown>;
  priority: number;
  enabled: boolean;
  auto_push: boolean;
}

/** How well the machine has been deciding, judged by what the customer did. */
export interface ShadowReport {
  decided: number;
  approved: number;
  rejected: number;
  /** null, not 100, when nothing was decided: no evidence is not a perfect score. */
  agreement_pct: number | null;
  days: number;
  rejections: {
    sku: string | null;
    suggested_price_cents: number | null;
    current_price_cents: number | null;
    at: string;
  }[];
  /** Whether the waiting week is over. There is no override. */
  ready: boolean;
  blocking: string[];
  alarms: number;
}

/** What undoing the last round WOULD do. Asking writes nothing. */
export interface RollbackPreview {
  batch_id: string | null;
  platform: string | null;
  pushed_at: string | null;
  /** Rows in that round that actually changed a price. */
  written: number;
  restore: number;
  skipped: number;
  by_reason: Record<string, number>;
}

/** The last round, read from the push log only (GET /pricing/rounds/last). */
export interface LastRound {
  batch_id: string;
  platform: string | null;
  pushed_at: string;
  written: number;
  /** Every row the round tried (the start marker left out). */
  total: number;
  /** A human already undid it (restored or failed). */
  rolled_back: boolean;
}

export interface RollbackResult {
  restored: number;
  skipped: number;
  failed: number;
}

/** Whether repricing may write right now, and whose stop is active. */
export interface AutomationState {
  stopped: boolean;
  /** 'platform' is ours and the customer cannot release it; 'tenant' is his. */
  stopped_by: 'platform' | 'tenant' | 'unreadable' | null;
  reason: string | null;
  tenant_stopped: boolean;
  /** The circuit-breaker limits that actually gate a round, read live. */
  brake: {
    max_change_pct: number;
    max_lower_pct: number;
    max_at_floor_pct: number;
    min_catalogue: number;
  };
  /** The automatic-sending tier; null = no rule was ever switched on. */
  tier: 'A' | 'B' | 'C' | null;
  tier_since: string | null;
}

// --- Review screen (Prijsadvies, paged server-side) ------------------------ #

export type ReviewBucket = 'safe' | 'check' | 'blocked';

/** A suggestion as the review list sends it: category, facet attributes and
 *  the signals that put it in its bucket ride along, so the page needs no
 *  second catalogue fetch. */
export interface PricingReviewItem extends PriceSuggestion {
  category: string;
  attributes: Record<string, unknown> | null;
  signals: string[];
  bucket: ReviewBucket;
  ean?: string | null;
  /** The lowest competitor the rule priced against (from the trace's signal
   *  stage); null without one. Optional: older servers leave it out. */
  competitor?: {
    retailer: string | null; price_cents: number; observed_at: string | null;
    /** Shops measured for this product. */
    observation_count?: number | null;
  } | null;
}

export interface PricingReviewSummary {
  /** Pass back to `review.decide`: only rows the screen actually showed move. */
  as_of: string;
  open: number;
  show_overview: boolean;
  /** A step this big (in %) is an outlier: the histogram paints it amber. */
  big_move_pct: number;
  buckets: Record<ReviewBucket, number>;
  by_signal: Record<string, number>;
  /** Per reason, the pending check proposals without a red signal: what a
   *  bulk approval of that reason really moves. */
  by_signal_approvable: Record<string, number>;
  histogram: { from_pct: number; to_pct: number; n: number }[];
  blocked_by_reason: { reason: string; n: number }[];
  categories: {
    category: string;
    safe: number;
    check: number;
    red: number;
    blocked: number;
    approved: number;
    /** Approved without a signal: "✓ N veilige goedgekeurd". */
    approved_safe: number;
    /** Outliers already approved or rejected. */
    reviewed: number;
    /** Mean step of the open proposals in %; null without one. */
    avg_step_pct: number | null;
    /** The rule behind most open proposals. */
    rule: string | null;
  }[];
}

export interface PricingReviewItemsParams {
  country: string;
  /** 'open' = pending + approved (the list's "Alles"). */
  status?: SuggestionStatus | 'open';
  bucket?: ReviewBucket;
  signal?: string;
  category?: string;
  q?: string;
  direction?: 'up' | 'down';
  own?: boolean;
  /** The facet sidebar's selection ({key: [values]}); sent as JSON. */
  facets?: Record<string, string[]>;
  sort?: 'impact' | 'recent';
  /** Server caps at 200. */
  limit?: number;
  offset?: number;
}

/** Query string for the review list endpoints: empty params dropped, the
 *  facet selection as JSON and only when it has keys. `category` keeps "":
 *  that is the uncategorized products, not "no filter". */
function reviewQuery({ facets, ...params }: PricingReviewItemsParams): URLSearchParams {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && (v !== '' || k === 'category')) qs.set(k, String(v));
  }
  if (facets && Object.keys(facets).length) qs.set('facets', JSON.stringify(facets));
  return qs;
}

export interface PricingReviewDecide {
  country: string;
  decision: 'approved' | 'rejected';
  /** The `as_of` of the summary the screen was built from. */
  as_of: string;
  /** Always explicit: a bulk decision never silently spans both buckets. */
  bucket: 'safe' | 'check';
  category?: string;
  signal?: string;
}

export interface PricingReadiness {
  rules_total: number;
  rules_enabled: number;
  monitored_skus: number;
  skus_with_floor: number;
  skus_without_floor: number;
  pending: number;
  approved: number;
  blocked: number;
  pushed: number;
  failed: number;
  /** ISO timestamp of the most recent suggestion generation, or null. */
  generated_at: string | null;
}

// --- Strategy hierarchy (shop default → categories → product exceptions) --- #

export interface StrategyCoverage {
  monitored: number;
  with_cost: number;
  with_floor: number;
  moves_up: number;
  moves_down: number;
}

export interface StrategyCategory {
  category_id: string | null;
  slug: string;
  name: string;
  monitored: number;
  with_cost: number;
  with_floor: number;
  moves_up: number;
  moves_down: number;
  /** The category's own rule, or null when it inherits the shop default. */
  rule: PricingRule | null;
}

export interface StrategyException {
  sku_id: string;
  sku: string;
  name: string;
  rule: PricingRule;
  moves_up: number;
  moves_down: number;
}

/** A facet-scoped rule: refines ONE category to the SKUs matching a filter
 *  selection (priority 250, between category and product). `monitored` is the
 *  live count of matching SKUs; `label` is the human filter text. */
export interface StrategyGroup {
  category_id: string | null;
  slug: string;
  category_name: string;
  label: string;
  monitored: number;
  rule: PricingRule;
}

export interface StrategyOverview {
  default_rule: PricingRule | null;
  coverage: StrategyCoverage;
  categories: StrategyCategory[];
  groups: StrategyGroup[];
  exceptions: StrategyException[];
}

/** One entry in the report list — enough to choose, not more. */
export interface ReportSummary {
  id: string;
  kind: 'weekly' | 'monthly';
  period_start: string;
  period_end: string;
  sent_at: string | null;
  recipient_count: number;
  headline_index: number | null;
}

/** The stored snapshot. `money` is null for a customer with neither cost nor
 *  sales: the money layer is then absent rather than zeroed, because zeros read
 *  as "you earned nothing" while absence reads as "not measured". */
export interface ReportPayload {
  period: { kind: 'weekly' | 'monthly'; start: string; end: string };
  coverage: {
    skus_total: number;
    skus_measured: number;
    pct: number | null;
    low_coverage_retailers: { slug: string; name: string; pct: number }[];
  };
  index: {
    overall: number | null;
    /** Same figure, each product weighted by what it earned. null without
     *  sales data — there is then nothing to weight by; absent entirely on
     *  reports stored before 2026-08-17. */
    weighted?: number | null;
    skus_counted: number;
    per_category: { category: string; index: number; skus: number }[];
    retailers_counted: { slug: string; name: string }[];
  };
  position: {
    undercut_skus: number;
    total_skus: number;
    retailers_total: number;
    cheapest_skus: number;
    /** Producten waar de goedkoopste markt-prijs exact de jouwe is. Zit in
     *  `cheapest_skus` (dat telt "niemand onder me") en staat hier apart voor
     *  de verdeling goedkoopst / gelijk / duurder. Absent op een rapport van
     *  vóór 2026-09-11. */
    tied_skus?: number;
  };
  /** The worklist the report opens with. `above_lowest` / `priced_skus` count
   *  the whole population, `items` is capped — a customer with 2000 products
   *  gets a list of 25 and a line saying how many more there are.
   *
   *  Optional, and it has to stay that way: a report stored before 2026-08-17
   *  has no such key, and a snapshot is never recalculated (that is the point
   *  of `report_runs.payload`). `ReportView` fills the gap; nothing else may
   *  assume the key is there. Same for the three below. */
  actions?: {
    items: {
      sku: string;
      sku_name: string;
      retailer: string;
      own_cents: number;
      lowest_cents: number;
      floor_cents: number | null;
      gap_cents: number;
      gap_pct: number;
      units: number;
      revenue_share_pct: number | null;
      advice: {
        kind: 'match' | 'follow' | 'floor_blocks';
        target_cents: number | null;
      };
    }[];
    above_lowest: number;
    priced_skus: number;
    revenue_share_pct: number | null;
    /** Dezelfde tellingen per categorie. Een klant met 1.180 producten en 29
     *  vergelijkbare heeft aan "2 van 29" weinig: de vraag is waar die twee
     *  zitten. Absent op een rapport van vóór 2026-09-11. */
    per_category?: {
      category: string;
      priced_skus: number;
      above_lowest: number;
      max_gap_cents: number | null;
      max_gap_sku: string | null;
    }[];
  };
  beaten_by?: { slug: string; name: string; beats: number; compared: number }[];
  /** Wat de markt deze periode bewoog, geteld. `movers` toont er drie per
   *  richting; dit zegt hoeveel het er waren. Absent vóór 2026-09-11. */
  price_changes?: { down: number; up: number; retailers: number };
  /** Dezelfde cijfers over de vorige periode, meegeslagen in de momentopname
   *  zodat een rapport zijn eigen verleden draagt en niet dat van vandaag.
   *  Absent vóór 2026-09-11. */
  previous?: {
    above_lowest: number;
    priced_skus: number;
    beaten_by: { slug: string; name: string; beats: number; compared: number }[];
  };
  /** De producten die je verkoopt maar niet geprijsd hebt. Elk ander cijfer in
   *  het rapport zet jouw prijs naast de markt en verdwijnt dus zodra die
   *  ontbreekt; hier staat wat de markt er wél deed. Optioneel: een rapport van
   *  vóór deze sectie draagt hem niet, en wordt nooit herberekend. */
  market_watch?: {
    total: number;
    shown: number;
    items: {
      sku: string;
      sku_name: string;
      brand: string | null;
      shops: number;
      market_cents: number | null;
      low_cents: number | null;
      low_retailer: string | null;
      delta_pct: number | null;
    }[];
    leaders: { name: string; count: number }[];
  };
  stock_opportunities?: {
    sku: string;
    sku_name: string;
    retailer: string;
    days_out: number;
    own_cents: number;
  }[];
  followup?: {
    period: { start: string; end: string };
    items: {
      sku: string;
      sku_name: string;
      from_cents: number;
      to_cents: number;
      units: number;
      margin_effect_cents: number;
    }[];
    missed: { count: number; cents: number };
  };
  /** `weighted` is absent on reports stored before 2026-08-17. */
  series: { label: string; value: number | null; weighted?: number | null }[];
  retailers: {
    slug: string;
    name: string;
    d30: number | null;
    d60: number | null;
    d90: number | null;
    coverage_pct: number | null;
  }[];
  movers: {
    direction: 'down' | 'up';
    pct: number;
    retailer: string;
    sku: string;
    sku_name: string;
  }[];
  reaction: { own_days: number | null; market_days: number | null };
  money: {
    margin_effect_cents: number;
    left_on_table_cents: number;
    pushes: {
      sku: string;
      sku_name: string;
      from_cents: number;
      to_cents: number;
      verified: boolean;
    }[];
    below_floor_now: { count: number; skus: { sku: string; sku_name: string }[] };
    margin_coverage: { with_cost: number; total: number };
    assumption: 'constant_volume';
  } | null;
}

export interface ReportDetail extends ReportSummary {
  payload: ReportPayload;
}

/** Scope for every Trends call: which products, and over which window. */
export interface TrendsScope {
  category?: string;
  facets?: FacetSelection;
  /** Inclusive YYYY-MM-DD. With `end`, this replaces the `days` preset. */
  start?: string;
  end?: string;
}

function trendsQuery(days: number, params?: TrendsScope): URLSearchParams {
  const qs = new URLSearchParams({ days: String(days) });
  if (params?.category) qs.set('category', params.category);
  if (params?.facets && Object.keys(params.facets).length) {
    qs.set('facets', JSON.stringify(params.facets));
  }
  // Both or neither — a half-filled range would silently fall back to `days`
  // and show a window nobody asked for.
  if (params?.start && params?.end) {
    qs.set('start', params.start);
    qs.set('end', params.end);
  }
  return qs;
}

export const api = {
  auth: {
    // Returns { access_token } on success, or { mfa_required: true, mfa_token }
    // when central auth requires a TOTP/recovery-code second step, or
    // { mfa_enrollment_required: true } when MFA hasn't been set up yet.
    login: (email: string, password: string, rememberMe: boolean) =>
      request<
        | { access_token: string }
        | { mfa_required: true; mfa_token: string }
        | { mfa_enrollment_required: true }
      >('POST', '/api/v1/auth/login', {
        email, password, remember_me: rememberMe,
      }),
    // Second step of central-auth MFA: submit the TOTP / recovery code.
    loginMfa: (mfaToken: string, code: string) =>
      request<{ access_token: string }>('POST', '/api/v1/auth/login/mfa', {
        mfa_token: mfaToken, code,
      }),
    me: () =>
      request<{
        user_id: string;
        email: string;
        name: string | null;
        role: Role;
        tenant_id: string;
        is_operator: boolean;
        /** The mail provider told us this address bounced or reported spam.
         *  Optional so the dozen existing test doubles of this response stay
         *  valid; the backend always sends it. */
        email_bounced?: boolean;
        /** The product groups this member looks after; empty = all. The
         *  Overview and Alerts open on these. Optional for the same reason. */
        category_ids?: string[];
      }>('GET', '/api/v1/auth/me'),
    requestReset: (email: string) =>
      request<void>('POST', '/api/v1/auth/password-reset/request', { email }),
    confirmReset: (token: string, password: string) =>
      request<void>('POST', `/api/v1/auth/password-reset/confirm/${token}`, { password }),
    previewInvite: (token: string) =>
      request<{ email: string; name: string }>('GET', `/api/v1/auth/invite/${token}`),
    // Response may be { access_token } (non-central) or { result: 'ok' } (central).
    acceptInvite: (token: string, password: string) =>
      request<{ access_token: string } | { result: string }>('POST', `/api/v1/auth/accept-invite/${token}`, { password }),
    /** Which company a claim link opens (a prepared trial account). Spends nothing. */
    previewClaim: (token: string) =>
      request<{ company: string }>('GET', `/api/v1/auth/claim/${token}`),
    /** Take over a prepared trial account: the placeholder becomes this person,
     *  the trial starts now, and the response signs him in. */
    claim: (
      token: string,
      body: { email: string; name: string; password: string; language: Locale; terms_version: string },
    ) => request<{ access_token: string }>('POST', `/api/v1/auth/claim/${token}`, body),
    /** Confirm the address from the sign-up mail. This is the call that
     *  creates the customer: organisation, administrator, trial and the rows
     *  recording what he agreed to, and it signs him in. */
    verify: (token: string) =>
      request<{ access_token: string }>('POST', `/api/v1/auth/verify/${token}`),
    invite: (email: string, name: string, role: Role) =>
      request<{ status: string; email: string }>('POST', '/api/v1/auth/invite', { email, name, role }),
    resendInvite: (email: string) =>
      request<{ status: string; email: string }>(
        'POST', `/api/v1/auth/invite/${encodeURIComponent(email)}/resend`),
    cancelInvite: (email: string) =>
      request<void>('POST', `/api/v1/auth/invite/${encodeURIComponent(email)}/cancel`),
  },
  realtime: {
    /** Mint a short-lived single-use ticket for the SSE handshake. The
     *  EventSource then connects with `?ticket=...` (browsers can't set
     *  custom headers on EventSource, so auth rides in the query string). */
    ticket: () => request<RealtimeTicketResponse>('POST', '/api/v1/realtime/ticket'),
  },
  skus: {
    list: (params?: {
      status?: SkuStatus;
      /** Accepts a slug, comma-list (gpu,psu), the sentinel '__none__'
       *  for Ongecategoriseerd, or a mix (gpu,__none__). */
      category?: string;
      /** Case-insensitive exact brand match. Unknown brand → empty. */
      brand?: string;
      limit?: number;
      offset?: number;
    }) => {
      const qs = new URLSearchParams();
      if (params?.status) qs.set('status', params.status);
      if (params?.category) qs.set('category', params.category);
      if (params?.brand) qs.set('brand', params.brand);
      if (params?.limit !== undefined) qs.set('limit', String(params.limit));
      if (params?.offset !== undefined) qs.set('offset', String(params.offset));
      const suffix = qs.toString() ? `?${qs}` : '';
      return request<SkuListResponse>('GET', `/api/v1/skus${suffix}`);
    },
    /** Bulk-update many SKUs in one transaction. At least one of
     *  `category_id` or `status` must be present. `category_id: null`
     *  moves them to Ongecategoriseerd. */
    bulkUpdate: (payload: SkuBulkUpdateRequest) =>
      request<SkuBulkUpdateResponse>('PATCH', '/api/v1/skus/bulk', payload),
    create: (data: SkuCreate) => request<Sku>('POST', '/api/v1/skus', data),
    importCsv: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return request<SkuCsvImportResponse>('POST', '/api/v1/skus/import', fd);
    },
    importPreview: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return request<SkuImportPreview>('POST', '/api/v1/skus/import/preview', fd);
    },
    // Mails the full problem-row list for a previewed upload. Does not consume
    // the upload token — the customer can still confirm the import afterwards.
    importIssuesReport: (uploadToken: string, mappings: SkuColumnMapping[]) =>
      request<SkuImportIssueReport>('POST', '/api/v1/skus/import/issues-report', {
        upload_token: uploadToken,
        mappings,
      }),
    importConfirm: (uploadToken: string, mappings: SkuColumnMapping[]) =>
      request<SkuCsvImportResponse>('POST', '/api/v1/skus/import/confirm', {
        upload_token: uploadToken,
        mappings,
      }),
    search: (q: string, limit = 10) => {
      const qs = new URLSearchParams({ q, limit: String(limit) });
      return request<SkuSearchHit[]>('GET', `/api/v1/skus/search?${qs}`);
    },
    get: (id: string) => request<Sku>('GET', `/api/v1/skus/${id}`),
    update: (id: string, payload: Partial<{ name: string; category: string; brand: string | null; status: SkuStatus; pinned: boolean }>) =>
      request<Sku>('PATCH', `/api/v1/skus/${id}`, payload),
    archive: (id: string) => request<void>('DELETE', `/api/v1/skus/${id}`),
    listings: (id: string, limit = 50) =>
      request<SkuLinkedListing[]>('GET', `/api/v1/skus/${id}/listings?limit=${limit}`),
    /** Set price-setup fields for one SKU/country. Floor/ceiling always allowed;
     *  cost/msrp 409 while a webshop is master. */
    setPrice: (id: string, payload: PriceSetupUpdate) =>
      request<SkuReferencePrice>('PATCH', `/api/v1/skus/${id}/price`, payload),
    /** Bulk-set the guardrail band (floor and/or ceiling) for a SKU-id list. */
    setFloors: (payload: BulkFloorRequest) =>
      request<BulkFloorResult>('POST', '/api/v1/skus/floors', payload),
  },
  retailers: {
    list: () => request<MonitoredRetailerListResponse>('GET', '/api/v1/retailers'),
    create: (payload: MonitoredRetailerCreate) =>
      request<MonitoredRetailer>('POST', '/api/v1/retailers', payload),
    update: (id: string, payload: MonitoredRetailerUpdate) =>
      request<MonitoredRetailer>('PATCH', `/api/v1/retailers/${id}`, payload),
    delete: (id: string) => request<void>('DELETE', `/api/v1/retailers/${id}`),
    scrapeLog: (id: string, limit = 20) =>
      request<ScrapeLog[]>('GET', `/api/v1/retailers/${id}/scrape-log?limit=${limit}`),
    mapSerper: (id: string) =>
      request<{ enqueued: number; est_serper_credits: number }>(
        'POST',
        `/api/v1/retailers/${id}/map-serper`,
      ),
    escalate: (id: string, payload: { geo_code?: string | null }) =>
      request<EscalateResponse>('POST', `/api/v1/retailers/${id}/escalate`, payload),
    healthHistory: (id: string, days = 14) =>
      request<HealthDay[]>('GET', `/api/v1/retailers/${id}/health-history?days=${days}`),
  },
  // V2: match endpoints removed — backend dropped them in Fase 1-2.
  // Frontend match-queue page replaced by /mapping/review (see api.mappings).
  // Stub kept so App.tsx tenant-bootstrap that calls api.match.queue() for
  // sidebar-badge gets a 0-count instead of network error during the
  // Fase 6 transition. Drop the stub once App.tsx swaps to api.mappings.review.
  match: {
    queue: async (_limit = 50, _offset = 0, _rejected = false) =>
      ({ items: [], total: 0 } as unknown as MatchQueueListResponse),
    stats: async () => ({ total: 0, critical: 0, accept_rate_30d: null }),
    accept: async (_listingId: string, _skuId: string) => ({ listings_updated: 0 }),
    reject: async (_listingId: string) => ({ listings_updated: 0 }),
  },
  mappings: {
    list: (params?: { sku_id?: string; monitored_retailer_id?: string; retailer?: string; state?: string; limit?: number; offset?: number }) => {
      const qs = new URLSearchParams();
      if (params?.sku_id) qs.set('sku_id', params.sku_id);
      if (params?.monitored_retailer_id) qs.set('monitored_retailer_id', params.monitored_retailer_id);
      if (params?.retailer) qs.set('retailer', params.retailer);
      if (params?.state) qs.set('state', params.state);
      if (params?.limit !== undefined) qs.set('limit', String(params.limit));
      if (params?.offset !== undefined) qs.set('offset', String(params.offset));
      const suffix = qs.toString() ? `?${qs}` : '';
      return request<MappingListResponse>('GET', `/api/v1/mappings${suffix}`);
    },
    get: (id: string) => request<Mapping>('GET', `/api/v1/mappings/${id}`),
    createManual: (payload: ManualMappingRequest) =>
      request<Mapping>('POST', '/api/v1/mappings', payload),
    // Canonical FSM transition (ADR-0025): operator marks matched / dismissed.
    updateState: (id: string, state: 'matched' | 'dismissed') =>
      request<Mapping>('PATCH', `/api/v1/mappings/${id}/state`, { state }),
    // Edit an existing mapping's URL in-place (re-validates EAN, gap A4).
    updateUrl: (id: string, payload: { listing_url: string; skip_ean_validation?: boolean }) =>
      request<Mapping>('PATCH', `/api/v1/mappings/${id}`, payload),
    delete: (id: string) => request<void>('DELETE', `/api/v1/mappings/${id}`),
    // "Weer leverbaar? Controleer nu": fetch this one page now.
    checkNow: (id: string) =>
      request<{ found_price: boolean }>('POST', `/api/v1/mappings/${id}/check`),
    // CSV-bulk URL import — multipart upload, returns the resolve-phase summary.
    importCsv: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return request<UrlImportResponse>('POST', '/api/v1/mappings/import', fd);
    },
    bulkRunStatus: (bulkRunId: string) =>
      request<BulkRunStatus>('GET', `/api/v1/mapping/bulk-runs/${bulkRunId}/status`),
    triggerJobs: (payload: TriggerMappingJobRequest) =>
      request<TriggerMappingJobResponse>('POST', '/api/v1/mappings/jobs', payload),
    review: {
      list: (limit = 50, offset = 0) =>
        request<ReviewQueueResponse>('GET', `/api/v1/mappings/review/queue?limit=${limit}&offset=${offset}`),
      resolve: (jobId: string, payload: ResolveReviewRequest) =>
        request<ReviewItem>('POST', `/api/v1/mappings/review/${jobId}`, payload),
    },
  },
  alerts: {
    list: (params?: {
      status?: AlertStatus[];
      severity?: AlertSeverity[];
      alert_type?: AlertType;
      category?: string;
      sort?: 'recent' | 'impact';
      limit?: number;
      offset?: number;
    }) => {
      const qs = new URLSearchParams();
      params?.status?.forEach((s) => qs.append('status', s));
      params?.severity?.forEach((s) => qs.append('severity', s));
      if (params?.alert_type) qs.set('alert_type', params.alert_type);
      if (params?.category) qs.set('category', params.category);
      if (params?.sort) qs.set('sort', params.sort);
      if (params?.limit !== undefined) qs.set('limit', String(params.limit));
      if (params?.offset !== undefined) qs.set('offset', String(params.offset));
      const suffix = qs.toString() ? `?${qs}` : '';
      return request<AlertListResponse>('GET', `/api/v1/alerts${suffix}`);
    },
    summary: () => request<AlertSummary>('GET', '/api/v1/alerts/summary'),
    /** Own-shop active promotions PriceQ derives from connector data (sell <
     *  msrp). Powers the "your own actions" panel on the Alerts page. */
    activePromos: () => request<ActivePromosResponse>('GET', '/api/v1/alerts/active-promos'),
    markRead: (id: string) => request<{ ok: boolean }>('POST', `/api/v1/alerts/${id}/read`),
    snooze: (id: string, days: number) =>
      request<{ ok: boolean }>('POST', `/api/v1/alerts/${id}/snooze`, { days }),
    ignore: (id: string, days: number) =>
      request<{ ok: boolean }>('POST', `/api/v1/alerts/${id}/ignore`, { days }),
    mute: (id: string) => request<{ ok: boolean; muted_count: number }>('POST', `/api/v1/alerts/${id}/mute`),
    resolve: (id: string) => request<{ ok: boolean }>('POST', `/api/v1/alerts/${id}/resolve`),
    scan: () => request<GenerateAlertsResponse>('POST', '/api/v1/alerts/scan'),
    listSettings: () => request<AlertSettingsResponse>('GET', '/api/v1/alert-settings'),
    updateSetting: (alertType: AlertType, payload: AlertSettingUpdate) =>
      request<AlertSetting>('PUT', `/api/v1/alert-settings/${alertType}`, payload),
    /** Drop one category's exception; the shop-wide rule governs it again. */
    deleteOverride: (alertType: AlertType, categoryId: string) =>
      request<{ ok: boolean }>(
        'DELETE',
        `/api/v1/alert-settings/${alertType}/categories/${categoryId}`,
      ),
  },
  categories: {
    list: () => request<CategoryListResponse>('GET', '/api/v1/categories'),
    /** Variant that returns sku_count per category + virtual rail row for
     *  Ongecategoriseerd. Backend payload shape differs (CategoryRailItem). */
    listWithCounts: () =>
      request<CategoryRailResponse>('GET', '/api/v1/categories?include_counts=true'),
    create: (payload: CategoryCreate) =>
      request<Category>('POST', '/api/v1/categories', payload),
    update: (id: string, payload: CategoryUpdate) =>
      request<Category>('PATCH', `/api/v1/categories/${id}`, payload),
    delete: (id: string) => request<void>('DELETE', `/api/v1/categories/${id}`),
    reorder: (ids: string[]) =>
      request<CategoryListResponse>('POST', '/api/v1/categories/reorder', { ids }),
  },
  facets: {
    keys: (categoryId: string) =>
      request<FacetKeysResponse>('GET', `/api/v1/facets/keys?category_id=${categoryId}`),
    values: (categoryId: string, keySlug: string, topN = 10) =>
      request<FacetValuesResponse>(
        'GET',
        `/api/v1/facets/values?category_id=${categoryId}&key_slug=${encodeURIComponent(keySlug)}&top_n=${topN}`,
      ),
    /** Fire-and-forget click telemetry — only call this when a value is
     *  switched ON (see FacetSidebar). Never awaited for its result by the
     *  caller: a failed log must not affect the filter itself. */
    usage: (categoryId: string, keySlug: string, value: string | null) =>
      request<void>('POST', '/api/v1/facets/usage', { category_id: categoryId, key_slug: keySlug, value }),
    // The five admin/*-gated calls that used to live here (proposals, decide,
    // startResearch, nameFill, health) moved to `adminOps.facets` below —
    // that surface takes the customer from the operator's URL instead of the
    // caller's own JWT (backend/admin/facets_api.py). See that block for why.
  },
  trends: {
    sku: (skuId: string, days = 90) =>
      request<SkuTrendResponse>('GET', `/api/v1/trends/sku/${skuId}?days=${days}`),
    /** `category` (slug) + `facets` scope the aggregated SKU set to the
     *  facet-filter sidebar's selection — both optional, both absent →
     *  identical to the un-scoped portfolio-wide view. */
    portfolio: (days = 30, params?: TrendsScope) =>
      request<TrendsPortfolioResponse>('GET', `/api/v1/trends/portfolio?${trendsQuery(days, params)}`),
    categoryRollup: (days = 30, params?: TrendsScope) =>
      request<CategoryRollupResponse>('GET', `/api/v1/trends/category-rollup?${trendsQuery(days, params)}`),
    movers: (days = 30, limit = 5, params?: TrendsScope) => {
      const qs = trendsQuery(days, params);
      qs.set('limit', String(limit));
      return request<TrendMoversResponse>('GET', `/api/v1/trends/top-movers?${qs}`);
    },
  },
  overview: {
    matrix: (params?: {
      category?: string;
      retailer_ids?: string[];
      brand?: string;
      only_pinned?: boolean;
      only_in_stock?: boolean;
      only_preorder?: boolean;
      only_below_list?: boolean;
      only_moved_recent?: boolean;
      only_stale?: boolean;
    }) => {
      const qs = new URLSearchParams();
      if (params?.category) qs.set('category', params.category);
      if (params?.brand) qs.set('brand', params.brand);
      if (params?.retailer_ids?.length)
        qs.set('retailer_ids', params.retailer_ids.join(','));
      if (params?.only_pinned) qs.set('only_pinned', 'true');
      if (params?.only_in_stock) qs.set('only_in_stock', 'true');
      if (params?.only_preorder) qs.set('only_preorder', 'true');
      if (params?.only_below_list) qs.set('only_below_list', 'true');
      if (params?.only_moved_recent) qs.set('only_moved_recent', 'true');
      if (params?.only_stale) qs.set('only_stale', 'true');
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return request<OverviewResponse>('GET', `/api/v1/overview${suffix}`);
    },
    cell: (skuId: string, retailerId: string, historyDays = 90) =>
      request<OverviewCellDetailResponse>(
        'GET',
        `/api/v1/overview/cell/${skuId}/${retailerId}?history_days=${historyDays}`,
      ),
    sellers: (skuId: string, retailerId: string) =>
      request<MarketplaceSellersResponse>(
        'GET',
        `/api/v1/overview/cell/${skuId}/${retailerId}/sellers`,
      ),
    exportCsv: (queryString: string) => {
      const suffix = queryString ? `?${queryString}` : '';
      return requestBlob('GET', `/api/v1/overview/export.csv${suffix}`);
    },
    sparklines: (params: { days?: number; category?: string }) => {
      const qs = new URLSearchParams();
      if (params.days !== undefined) qs.set('days', String(params.days));
      if (params.category) qs.set('category', params.category);
      const suffix = qs.toString() ? `?${qs}` : '';
      return request<OverviewSparklinesResponse>('GET', `/api/v1/overview/sparklines${suffix}`);
    },
    leaderboard: (params: { category?: string; brand?: string }) => {
      const qs = new URLSearchParams();
      if (params.category) qs.set('category', params.category);
      if (params.brand) qs.set('brand', params.brand);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return request<LeaderboardResponse>(
        'GET',
        `/api/v1/overview/leaderboard${suffix}`,
      );
    },
  },
  tenants: {
    me: () => request<TenantResponse>('GET', '/api/v1/tenants/me'),
    /** Admin-only server-side (require_admin). `home_country` decides which
     *  retailers the customer may add at all, so it has to be changeable. */
    update: (fields: { home_country?: string; name?: string } & TenantCompanyDetails) =>
      request<TenantResponse>('PATCH', '/api/v1/tenants/me', fields),
    /** Ask for this customer's own account to be deleted. Admin-only
     *  server-side, and `confirm` must be the organisation's name typed out —
     *  the endpoint refuses with 422 otherwise. Nothing is deleted today:
     *  `scheduled_for` is thirty days out and `requestDeletionCancel` undoes
     *  it until then. */
    requestDeletion: (confirm: string) =>
      request<{ requested_at: string; scheduled_for: string }>(
        'POST', '/api/v1/tenants/me/deletion', { confirm },
      ),
    cancelDeletion: () => request<void>('DELETE', '/api/v1/tenants/me/deletion'),
  },
  // Stripe Billing owns changing plan, cancelling and resuming now (T9) — the
  // portal (below) does all three, plus the proration the old hand-rolled
  // /me/subscription/* endpoints deliberately skipped.
  billing: {
    status: () => request<BillingStatus>('GET', '/api/v1/billing/status'),
    /** Rate-limited server-side (10/hour): every call creates a Checkout
     *  session at Stripe. Enterprise is deliberately not a valid `plan` —
     *  it is a negotiated contract, never self-service. */
    checkout: (plan: 'start' | 'business' | 'professional', interval: 'month' | 'year' = 'month') =>
      request<{ url: string }>('POST', '/api/v1/billing/checkout', { plan, interval }),
    /** `flow` stuurt de klant rechtstreeks naar die ene handeling bij Stripe,
     *  in plaats van naar een menu waar hij hem nog moet zoeken. */
    portal: (flow?: 'payment_method_update' | 'subscription_cancel' | 'subscription_update') =>
      request<{ url: string }>(
        'POST',
        flow ? `/api/v1/billing/portal?flow=${flow}` : '/api/v1/billing/portal',
        {},
      ),
    /** The payment page's own confirmation for switching Marktplaats-inzicht
     *  on or off. Nothing changes until the customer confirms there. */
    marketplaceInsight: (on: boolean) =>
      request<{ url: string }>('POST', '/api/v1/billing/marketplace-insight', { on }),
    /** Het adres van één factuur in de boekhouding. `reference` is de
     *  `reference` van een regel uit `status.payments` -- die kiest WELKE regel
     *  gelezen wordt; van wie hij is bepaalt de server uit de sessie.
     *
     *  `?format=json` en niet de 302 die dezelfde route standaard geeft: deze
     *  app stuurt een bearer-token mee, en een `fetch` die de omleiding volgt
     *  landt op moneybird.com zonder CORS-headers en mislukt. Een gewone link
     *  zou helemaal geen token meesturen. */
    invoice: (reference: string) =>
      request<{ url: string }>(
        'GET',
        `/api/v1/billing/invoices/${encodeURIComponent(reference)}?format=json`,
      ),
  },
  me: {
    entitlements: () => request<Entitlements>('GET', '/api/v1/me/entitlements'),
    marketplaceAccounts: () =>
      request<MarketplaceAccountsState>('GET', '/api/v1/me/marketplace-accounts'),
    setMarketplaceAccount: (slug: 'amazon' | 'bol' | 'mediamarkt', seller: string) =>
      request<MarketplaceAccountsState>('PUT', `/api/v1/me/marketplace-accounts/${slug}`, { seller }),
    deleteMarketplaceAccount: (slug: 'amazon' | 'bol' | 'mediamarkt') =>
      request<void>('DELETE', `/api/v1/me/marketplace-accounts/${slug}`),
    setup: () => request<SetupState>('GET', '/api/v1/me/setup'),
    /** Start a price round for this customer's own products, now. Spends
     *  measuring room, so the backend refuses a second one while the first
     *  runs (409) and refuses both once the day's room is gone (429). */
    measureNow: () => request<MeasureNowState>('POST', '/api/v1/me/measure-now'),
    measureNowState: () => request<MeasureNowState>('GET', '/api/v1/me/measure-now'),
    scanSchedule: () => request<ScanSchedule>('GET', '/api/v1/me/scan-schedule'),
    setScanSchedule: (secondScanHourUtc: number) =>
      request<{ second_scan_hour: number }>('PUT', '/api/v1/me/scan-schedule', {
        second_scan_hour: secondScanHourUtc,
      }),
    /** The language this account reads in. Stored server-side, not only in
     *  this browser: the periodic report is sent by a job with no browser to
     *  ask, and it used to default everyone to Dutch. */
    language: () => request<{ language: Locale }>('GET', '/api/v1/me/language'),
    setLanguage: (language: Locale) =>
      request<{ language: Locale }>('PUT', '/api/v1/me/language', { language }),
    /** Who this person is and what kind of customer he works for. `complete`
     *  is false while his own name is still empty -- signing up asks for a
     *  company and an address and nothing else. */
    profile: () => request<Profile>('GET', '/api/v1/me/profile'),
    saveProfile: (body: ProfileInput) =>
      request<{ status: string }>('PUT', '/api/v1/me/profile', body),
    /** Which legal documents this person has not agreed to at their current
     *  version. Empty means he is up to date. */
    terms: () => request<TermsStatus>('GET', '/api/v1/me/terms'),
    /** No body: agreeing is for the person in the session, for whatever is
     *  open. A request that could name someone else could agree for them. */
    acceptTerms: () => request<void>('POST', '/api/v1/me/terms', {}),
  },
  /** The legal documents. Public: the terms have to be readable before there
   *  is an account, or handing them over has not happened. */
  legal: {
    doc: (doc: LegalDocSlug, lang: Locale) =>
      request<LegalDocument>('GET', `/api/v1/legal/${doc}?lang=${lang}`),
    rawUrl: (doc: LegalDocSlug, lang: Locale) => `${BASE}/api/v1/legal/${doc}/raw?lang=${lang}`,
    company: () => request<CompanyDetails>('GET', '/api/v1/legal/company'),
    terms: () => request<CurrentTerms>('GET', '/api/v1/legal/terms'),
  },
  signup: {
    /** Always answers 202, whatever happens -- see backend/signup/router.py.
     *  The only error it can return is a password found in a known breach. */
    register: (body: SignupInput) =>
      request<{ status: string }>('POST', '/api/v1/signup', body),
    /** Stuurt de bevestigingsmail opnieuw. Ook altijd 202: een ander antwoord
     *  voor een bekend adres zou verraden wie er klant is. */
    resend: (email: string) =>
      request<{ status: string }>('POST', '/api/v1/signup/resend', { email }),
  },
  /** The in-app report button (klant-feedback-lus). Tenant and user come from
   *  the session; the backend refuses a body that names either. */
  meldingen: {
    vrijgave: () => request<MeldVrijgave>('GET', '/api/v1/meldingen/vrijgave'),
    lijst: (team: boolean) => request<MeldingLijst>('GET', `/api/v1/meldingen${team ? '?team=true' : ''}`),
    detail: (externalId: string) =>
      request<MeldingDetail>('GET', `/api/v1/meldingen/${encodeURIComponent(externalId)}`),
    maak: (body: NieuweMelding) => request<Verstuurd>('POST', '/api/v1/meldingen', body),
    bericht: (externalId: string, body: NieuwBericht) =>
      request<Verstuurd>('POST', `/api/v1/meldingen/${encodeURIComponent(externalId)}/berichten`, body),
  },
  auditLog: {
    list: (limit = 50, offset = 0) =>
      request<AuditLogListResponse>('GET', `/api/v1/audit-log?limit=${limit}&offset=${offset}`),
  },
  exports: {
    /** Everything this customer owns, as a ZIP of CSVs. Can take a while on a
     *  large catalogue — the caller shows a busy state rather than a timeout. */
    full: () => requestBlob('GET', '/api/v1/exports/full'),
    /** One report as an XLSX workbook over the last `days` days. Timestamps end
     *  in Z rather than +00:00: a plus sign in a query string decodes to a
     *  space and the server would reject the range. */
    report: (type: ReportType, days: number) => {
      const to = new Date();
      const from = new Date(to.getTime() - days * 86_400_000);
      const iso = (d: Date) => d.toISOString().replace(/\.\d+Z$/, 'Z');
      return requestBlob(
        'GET',
        `/api/v1/exports/report/${type}?from=${iso(from)}&to=${iso(to)}`,
      );
    },
  },
  reports: {
    /** The periodic reports that were sent, newest first. */
    list: () => request<ReportSummary[]>('GET', '/api/v1/reports'),
    /** One report's stored snapshot. The figures were computed when the report
     *  was sent and are never recalculated — that is why the mail from three
     *  months ago and this page still agree. */
    get: (id: string) => request<ReportDetail>('GET', `/api/v1/reports/${id}`),
    /** Recompute the snapshot for the last completed period.
     *
     *  A report is a snapshot, which is what makes an old mail and this page
     *  agree — but it also means a report stored before a metric shipped will
     *  never show that metric. Rebuilding is how you get today's figures with
     *  today's code. It does not resend the mail. */
    rebuild: (kind: 'weekly' | 'monthly' = 'weekly') =>
      request<ReportSummary>('POST', `/api/v1/reports/rebuild?kind=${kind}`),
    pdf: (id: string) => requestBlob('GET', `/api/v1/reports/${id}.pdf`),
    xlsx: (id: string) => requestBlob('GET', `/api/v1/reports/${id}.xlsx`),
  },
  apiKeys: {
    list: () => request<ApiKey[]>('GET', '/api/v1/api-keys'),
    /** The response carries the key itself. It is the only one that ever will —
     *  only the hash is stored, so there is nothing to show again. */
    create: (name: string) => request<ApiKeyCreated>('POST', '/api/v1/api-keys', { name }),
    revoke: (id: string) => request<ApiKey>('POST', `/api/v1/api-keys/${id}/revoke`),
  },
  /** AI-app connector (Claude, ChatGPT): status, consent and connections. */
  connect: {
    status: () => request<ConnectStatus>('GET', '/api/v1/connect/status'),
    client: (clientId: string) =>
      request<{ client_name: string; redirect_host: string; verified: boolean }>(
        'GET', `/api/v1/connect/client/${encodeURIComponent(clientId)}`,
      ),
    approve: (body: ConnectApproveIn) => request<{ redirect_to: string }>('POST', '/api/v1/connect/approve', body),
    grants: () => request<ConnectGrant[]>('GET', '/api/v1/connect/grants'),
    revoke: (id: string) => request<void>('DELETE', `/api/v1/connect/grants/${id}`),
    permissions: () => request<AiPermissions>('GET', '/api/v1/connect/permissions'),
    /** `level: null` removes a tool's own choice: it follows its group again. */
    setPermission: (body: { key: string; level: AiLevel | null; accepted_warning?: string }) =>
      request<void>('PUT', '/api/v1/connect/permissions', body),
    pending: () => request<AiPendingAction[]>('GET', '/api/v1/connect/pending'),
    pendingOne: (id: string) => request<AiPendingAction>('GET', `/api/v1/connect/pending/${id}`),
    decide: (id: string, body: { decision: 'approve' | 'reject'; confirm?: string; effect_hash?: string | null }) =>
      request<AiPendingAction>('POST', `/api/v1/connect/pending/${id}/decide`, body),
  },
  savedViews: {
    list: (scope: string) =>
      request<SavedView[]>('GET', `/api/v1/saved-views?scope=${encodeURIComponent(scope)}`),
    save: (body: { scope: string; name: string; filter_json: Record<string, string> }) =>
      request<SavedView>('POST', '/api/v1/saved-views', body),
    remove: (id: string) => request<void>('DELETE', `/api/v1/saved-views/${id}`),
  },
  team: {
    listMembers: (includeInactive = false) =>
      request<TeamMember[]>('GET', `/api/v1/team/members?include_inactive=${includeInactive}`),
    changeRole: (userId: string, role: Role) =>
      request<{ status: string; role: string }>('PATCH', `/api/v1/team/members/${userId}/role`, { role }),
    deactivate: (userId: string) =>
      request<{ status: string }>('POST', `/api/v1/team/members/${userId}/deactivate`),
    reactivate: (userId: string) =>
      request<{ status: string }>('POST', `/api/v1/team/members/${userId}/reactivate`),
    /** Empty list = all categories. Yourself always; someone else needs supervisor. */
    setCategories: (userId: string, categoryIds: string[]) =>
      request<{ status: string; category_ids: string[] }>(
        'PUT', `/api/v1/team/members/${userId}/categories`, { category_ids: categoryIds },
      ),
    /** Remove yourself from this organisation. Revokes the session server-side
     *  (token_version bump), so the caller must clear its token and sign out. */
    leave: () => request<{ status: string }>('POST', '/api/v1/team/leave'),
  },
  admin: {
    health: () => request<AdminHealthResponse>('GET', '/api/v1/admin/health'),
    spendSummary: (days = 7) =>
      request<AdminSpendResponse>('GET', `/api/v1/admin/spend-summary?days=${days}`),
  },
  /** Operator-only. Separate from `admin` above, which predates this and only
   *  carries health + spend: one name for two surfaces is how the first admin
   *  gap happened. */
  adminOps: {
    /** Six counts for the sidebar pills and the Vandaag page. */
    summary: () => request<AdminSummary>('GET', '/api/v1/admin/summary'),
    tenants: {
      list: () => request<AdminTenantRow[]>('GET', '/api/v1/admin/tenants'),
      get: (id: string) => request<AdminTenantDetail>('GET', `/api/v1/admin/tenants/${id}`),
      create: (body: AdminNewTenant) =>
        request<AdminTenantDetail>('POST', '/api/v1/admin/tenants', body),
      ops: (id: string) => request<AdminTenantOps>('GET', `/api/v1/admin/tenants/${id}/ops`),
      audit: (id: string, limit = 50) =>
        request<AdminAuditRow[]>('GET', `/api/v1/admin/tenants/${id}/audit?limit=${limit}`),
      /** Start a look-along: a read-only token on this customer, 30 minutes.
       *  The reason is mandatory (the backend wants 5 characters) and lands in
       *  an audit row the CUSTOMER can read back. 404 = unknown customer,
       *  409 = archived, 503 = the reason could not be recorded so the session
       *  was not started. */
      impersonate: (tenantId: string, reason: string) =>
        request<ImpersonationGrant>(
          'POST', `/api/v1/admin/tenants/${tenantId}/impersonate`, { reason },
        ),
      /** Active <-> archived. `reason` is required and is the only place the
       *  why of this change is ever written down -- `tenants` keeps no history
       *  of its own. 409 `already_in_state` = he stood there already and
       *  nothing happened; 503 `not_recorded` = the record failed, so NOTHING
       *  was written; 503 `cannot_read_customers` = this connection may not
       *  look across customers, so a refusal is not proof the customer is gone.
       *  `suspended` and `deleted` are not offered: the nightly sweep writes
       *  the first and nobody writes the second. */
      setStatus: (tenantId: string, status: 'active' | 'archived', reason: string) =>
        request<AdminTenantDetail>(
          'POST', `/api/v1/admin/tenants/${tenantId}/status`, { status, reason },
        ),
      /** A date moves the end of the trial; `null` means THIS CUSTOMER PAYS and
       *  clears the trial plus sets him active in one act. That second form is
       *  for payment outside the checkout only -- a customer who pays in the
       *  app has both fields written by the payment webhook already. */
      setTrial: (tenantId: string, endsAt: string | null, reason: string) =>
        request<AdminTenantDetail>(
          'POST', `/api/v1/admin/tenants/${tenantId}/trial`, { ends_at: endsAt, reason },
        ),
      /** The operator's own note. An empty string clears it. No reason: the
       *  note IS the prose, and the record deliberately carries its length
       *  rather than its text. */
      setNote: (tenantId: string, text: string) =>
        request<AdminTenantDetail>(
          'PUT', `/api/v1/admin/tenants/${tenantId}/notes`, { text },
        ),
      /** What this customer may do: his plan, the RESOLVED ceilings and the
       *  raw flags. `source` per ceiling says whether the plan spoke or an
       *  exception someone once granted. */
      /** Wat deze klant betaalt: dezelfde lezing die de klant zelf op zijn
       *  instellingenpagina krijgt, plus de twee velden die alleen een
       *  medewerker nodig heeft. Leest alleen -- verplaatst geen geld.
       *  `state: 'none'` is GEEN fout: een klant in zijn proefperiode en
       *  iedereen van vóór de facturatie heeft geen abonnement. */
      billing: (tenantId: string) =>
        request<AdminBillingStatus>('GET', `/api/v1/admin/tenants/${tenantId}/billing`),
      /** What a plan change would cost, and against which instant. Writes
       *  nothing -- the whole point is that no money moves before a person has
       *  read the figure. Hand `proration_date` back untouched on the confirm;
       *  without it the provider prices against the click instead of the read. */
      billingPreview: (tenantId: string, body: { plan: string; interval: BillingInterval }) =>
        request<AdminPlanPreview>(
          'POST', `/api/v1/admin/tenants/${tenantId}/billing/plan/preview`, body,
        ),
      /** The confirm, carrying back what was on screen. A different figure is a
       *  refusal (`409 amount_changed`), never a silent repricing. */
      billingChangePlan: (
        tenantId: string,
        body: {
          plan: string;
          interval: BillingInterval;
          shown_amount_cents: number;
          proration_date: number;
          reason: string;
        },
      ) =>
        request<AdminSubscriptionState>(
          'POST', `/api/v1/admin/tenants/${tenantId}/billing/plan`, body,
        ),
      /** `immediate: false` stops the subscription at the end of the period and
       *  can be undone until then; `true` is the other action entirely -- access
       *  goes at once and no money comes back by itself. */
      billingCancel: (tenantId: string, body: { reason: string; immediate: boolean }) =>
        request<AdminSubscriptionState>(
          'POST', `/api/v1/admin/tenants/${tenantId}/billing/cancel`, body,
        ),
      billingResume: (tenantId: string, body: { reason: string }) =>
        request<AdminSubscriptionState>(
          'POST', `/api/v1/admin/tenants/${tenantId}/billing/resume`, body,
        ),
      entitlements: (tenantId: string) =>
        request<AdminEntitlements>('GET', `/api/v1/admin/tenants/${tenantId}/entitlements`),
      /** One flag, with a reason. `plan` is itself a flag, so this is also the
       *  plan switch -- and in this phase it changes only what the customer MAY
       *  do, never what he pays (`backend/billing/` is untouched). */
      setFeature: (tenantId: string, key: string, value: string, reason: string) =>
        request<AdminEntitlements>(
          'PUT', `/api/v1/admin/tenants/${tenantId}/features/${key}`, { value, reason },
        ),
    },
    users: {
      list: (tenantId: string) =>
        request<AdminUserRow[]>('GET', `/api/v1/admin/tenants/${tenantId}/users`),
      invite: (tenantId: string, body: { email: string; name: string; role: Role }) =>
        request<AdminUserRow>('POST', `/api/v1/admin/tenants/${tenantId}/users`, body),
      setRole: (tenantId: string, userId: string, role: Role) =>
        request<AdminUserRow>('PATCH', `/api/v1/admin/tenants/${tenantId}/users/${userId}`, { role }),
      deactivate: (tenantId: string, userId: string) =>
        request<AdminUserRow>('POST', `/api/v1/admin/tenants/${tenantId}/users/${userId}/deactivate`),
      reactivate: (tenantId: string, userId: string) =>
        request<AdminUserRow>('POST', `/api/v1/admin/tenants/${tenantId}/users/${userId}/reactivate`),
      resendInvite: (tenantId: string, userId: string) =>
        request<{ status: string }>('POST', `/api/v1/admin/tenants/${tenantId}/users/${userId}/invite-resend`),
      resetLink: (tenantId: string, userId: string) =>
        request<{ status: string }>('POST', `/api/v1/admin/tenants/${tenantId}/users/${userId}/reset-link`),
      // Signs this user out everywhere by raising his token_version; he keeps
      // his password. Answers the updated row, like the four above it.
      revokeSessions: (tenantId: string, userId: string) =>
        request<AdminUserRow>('POST', `/api/v1/admin/tenants/${tenantId}/users/${userId}/revoke-sessions`),
      // The login backoff on this user's address. It lives in a counter, not
      // in the database, so this read is the only way to see it at all.
      lockout: (tenantId: string, userId: string) =>
        request<AdminUserLockout>('GET', `/api/v1/admin/tenants/${tenantId}/users/${userId}/lockout`),
      clearLockout: (tenantId: string, userId: string) =>
        request<AdminUserLockout>('DELETE', `/api/v1/admin/tenants/${tenantId}/users/${userId}/lockout`),
      // Moves the login address AND revokes that user's sessions, in one
      // backend action -- see admin/users_api.py::change_user_email. The
      // screen has to say so; the call cannot do one without the other.
      changeEmail: (tenantId: string, userId: string, email: string) =>
        request<AdminUserRow>('PATCH', `/api/v1/admin/tenants/${tenantId}/users/${userId}/email`, { email }),
    },
    signups: {
      // pendingOnly defaults to true, matching signup/router.py::list_signups's
      // own default -- an existing caller that never passes this keeps seeing
      // pending-only rows.
      list: (pendingOnly = true) =>
        request<AdminSignup[]>('GET', `/api/v1/admin/signups?pending_only=${pendingOnly}`),
      // tenant_id is nullable: an "already_approved" reply for a signup whose
      // provisioning has not finished yet (see signup/router.py) carries no
      // tenant_id yet either.
      approve: (id: string) =>
        request<{ status: string; tenant_id: string | null; slug?: string }>(
          'POST', `/api/v1/admin/signups/${id}/approve`),
      reject: (id: string) => request<{ status: string }>('POST', `/api/v1/admin/signups/${id}/reject`),
    },
    problems: {
      list: (state: 'open' | 'resolved' = 'open', tenantId?: string) =>
        request<AdminCustomerProblem[]>(
          'GET',
          `/api/v1/admin/problems?state=${state}${tenantId ? `&tenant_id=${encodeURIComponent(tenantId)}` : ''}`,
        ),
      resolve: (id: string) =>
        request<AdminCustomerProblem>('POST', `/api/v1/admin/problems/${id}/resolve`),
    },
    ladder: {
      list: () => request<AdminLadderRow[]>('GET', '/api/v1/admin/ladder'),
      setBrowser: (slug: string, country: string, browser: boolean) =>
        request<AdminLadderRow>(
          'PATCH',
          `/api/v1/admin/ladder/${encodeURIComponent(slug)}/${encodeURIComponent(country)}/browser`,
          { browser },
        ),
    },
    // Shapes verified against backend/admin/support_schemas.py and
    // support_api.py directly, not from the phase-2 design sketch: every
    // mutation below returns the full SupportTicketDetail (not `{status}`),
    // and `list`'s status filter is a query param named `status` even though
    // the backend parameter is called `ticket_status` internally (it shadows
    // the `status` module there — see support_api.py's comment).
    support: {
      list: (params?: { status?: string; tenantId?: string }) => {
        const qs = new URLSearchParams();
        if (params?.status) qs.set('status', params.status);
        if (params?.tenantId) qs.set('tenant_id', params.tenantId);
        const suffix = qs.toString() ? `?${qs}` : '';
        return request<AdminSupportTicketRow[]>('GET', `/api/v1/admin/support${suffix}`);
      },
      get: (id: string) => request<AdminSupportTicketDetail>('GET', `/api/v1/admin/support/${id}`),
      reply: (id: string, bodyText: string) =>
        request<AdminSupportTicketDetail>('POST', `/api/v1/admin/support/${id}/reply`, {
          body_text: bodyText,
        }),
      note: (id: string, bodyText: string) =>
        request<AdminSupportTicketDetail>('POST', `/api/v1/admin/support/${id}/note`, {
          body_text: bodyText,
        }),
      setStatus: (id: string, status: AdminSupportTicketStatus) =>
        request<AdminSupportTicketDetail>('POST', `/api/v1/admin/support/${id}/status`, { status }),
      assign: (id: string, userId: string | null) =>
        request<AdminSupportTicketDetail>('POST', `/api/v1/admin/support/${id}/assign`, {
          user_id: userId,
        }),
      linkTenant: (id: string, tenantId: string | null) =>
        request<AdminSupportTicketDetail>('POST', `/api/v1/admin/support/${id}/link-tenant`, {
          tenant_id: tenantId,
        }),
    },
    // Operator variant of the filter-approval screen (backend/admin/facets_api.py,
    // task 1 of the fase-3 filters plan) — every call takes the customer from
    // the URL (`tenantId`), never from the caller's own JWT. Shapes verified
    // against backend/admin/facets_api.py directly, not the design sketch (see
    // that file's module docstring for why /api/v1/categories and the old
    // /api/v1/admin/facets/* routes could not be reused for an operator).
    // This used to name a second source, `admin/facets_schemas.py`. That file
    // is gone: it only held the request body of the per-customer decision
    // route, and fase 4 removed that route. The decision body that still
    // exists belongs to the SHARED category
    // (`admin/node_filters_schemas.py::NodeKeyDecisionIn`, used by
    // `adminOps.categoryNodes` below), not to this group — nothing here posts
    // a decision any more.
    facets: {
      /** Not in the original plan — added here because `api.categories.list()`
       *  reads its customer from the caller's own JWT, which for an operator
       *  is always the platform tenant, never the customer chosen on screen.
       *  See backend/admin/facets_api.py::list_categories. */
      categories: (tenantId: string) =>
        request<CategoryListResponse>('GET', `/api/v1/admin/tenants/${tenantId}/facets/categories`),
      proposals: (tenantId: string, categoryId: string) =>
        request<FacetProposalsResponse>(
          'GET',
          `/api/v1/admin/tenants/${tenantId}/facets/proposals?category_id=${categoryId}`,
        ),
      /** Kicks off a new proposal run for this customer + category. Costs real
       *  money at external suppliers — 409 (not this promise rejecting quietly)
       *  when a run for the same customer+category is already queued. */
      startResearch: (tenantId: string, categoryId: string) =>
        request<FacetResearchResponse>('POST', `/api/v1/admin/tenants/${tenantId}/facets/research/${categoryId}`),
      /** Fills this category's filter values from the product names. No AI,
       *  no fetch cost — safe to re-run. Still 409s while a run is busy. */
      nameFill: (tenantId: string, categoryId: string) =>
        request<FacetResearchResponse>('POST', `/api/v1/admin/tenants/${tenantId}/facets/name-fill/${categoryId}`),
      /** Manufacturer specs — costs real money at external suppliers. */
      vendorSpecs: (tenantId: string, categoryId: string) =>
        request<FacetResearchResponse>(
          'POST',
          `/api/v1/admin/tenants/${tenantId}/facets/vendor-specs/${categoryId}`,
        ),
      /** Platform specs (chip/chipset) — costs real money at external suppliers. */
      platformSpecs: (tenantId: string, categoryId: string) =>
        request<FacetResearchResponse>(
          'POST',
          `/api/v1/admin/tenants/${tenantId}/facets/platform-specs/${categoryId}`,
        ),
      /** What the filter bar actually holds. Read-only, costs nothing. */
      health: (tenantId: string, categoryId: string) =>
        request<FacetHealthResponse>(
          'GET',
          `/api/v1/admin/tenants/${tenantId}/facets/health?category_id=${categoryId}`,
        ),
      /** What the filter bar would look like today vs. after fase 3 switches
       *  the read path to the shared library. Read-only, costs nothing. See
       *  backend/admin/library_compare.py. */
      libraryCompare: (tenantId: string, categoryId: string) =>
        request<LibraryCompareResponse>(
          'GET',
          `/api/v1/admin/tenants/${tenantId}/facets/library-compare?category_id=${categoryId}`,
        ),
      /** Which spellings of a filter value got folded into one choice, per
       *  approved key (backend/admin/facet_value_groups_api.py, issue #480).
       *  Read-only, costs nothing. The server's ORDER is the work order --
       *  the biggest groups first, because a group of one cannot be wrong. */
      valueGroups: (tenantId: string, categoryId: string) =>
        request<AdminValueGroupsResponse>(
          'GET',
          `/api/v1/admin/tenants/${tenantId}/facets/value-groups?category_id=${categoryId}`,
        ),
      /** Says where one spelling belongs: in the group `into`, or -- with
       *  `into: null` -- on its own. One call for both, because detaching
       *  alone leaves a value stranded instead of in the group it belongs to.
       *
       *  `reason` must be non-empty or the server answers 422; 404 carries a
       *  `code` (`unknown_key`, `unknown_value`, `unknown_target`) and 503
       *  `not_recorded` means NOTHING was written. The answer is that key's
       *  rebuilt groups. */
      assignValueGroup: (
        tenantId: string,
        categoryId: string,
        keySlug: string,
        body: { value: string; into: string | null; reason: string },
      ) =>
        request<AdminValueGroupKey>(
          'POST',
          `/api/v1/admin/tenants/${tenantId}/facets/value-groups/${encodeURIComponent(keySlug)}/assign?category_id=${categoryId}`,
          body,
        ),
    },
    // A customer's webhook endpoints and what happened to their deliveries
    // (backend/admin/webhooks_api.py). Shapes verified against
    // backend/admin/webhooks_schemas.py directly, not the phase-6 plan's
    // sketch -- the plan called the column `events`; it is `event_types`.
    //
    // `retry` SENDS REAL CUSTOMER DATA to an address outside our systems, so
    // it is the one call in `adminOps` a caller must confirm first. The
    // backend refuses anything that is not a dead letter with a 409 (a row
    // lock plus a status gate), and answers 503 when the attempt could not be
    // recorded -- in which case NOTHING was sent. A caller that shows one
    // generic "er ging iets mis" for all three is lying about at least two.
    webhooks: {
      list: (tenantId: string, limit = 50) =>
        request<AdminWebhooksResponse>(
          'GET', `/api/v1/admin/tenants/${tenantId}/webhooks?limit=${limit}`,
        ),
      retry: (tenantId: string, deliveryId: string) =>
        request<AdminWebhookDeliveryRow>(
          'POST', `/api/v1/admin/tenants/${tenantId}/webhooks/deliveries/${deliveryId}/retry`,
        ),
    },
    // Every customer category and the shared category it hangs on
    // (backend/admin/category_links_api.py). The list is cross-customer by
    // design and reads through the RLS-bypassing role behind require_operator.
    //
    // The server's ORDER is the work order -- unlinked first, then the AI
    // links with the least certainty. A caller that re-sorts throws away the
    // only thing that makes this list scannable.
    //
    // Both writes name the customer as well as the category: a category id
    // from a URL says nothing about who it belongs to, and the backend 404s
    // a category that belongs to someone else. The customer therefore comes
    // from the ROW, never from a chooser at the top of the screen.
    categoryLinks: {
      list: (tenantId?: string, limit = 200) => {
        const qs = new URLSearchParams({ limit: String(limit) });
        if (tenantId) qs.set('tenant_id', tenantId);
        return request<AdminCategoryLinksResponse>('GET', `/api/v1/admin/category-links?${qs}`);
      },
      /** Every shared category there is, used or not -- the picker cannot be
       *  built from the links alone, or the ones nobody has reached yet stay
       *  unreachable by hand. */
      nodes: () => request<AdminCategoryNode[]>('GET', '/api/v1/admin/category-nodes'),
      /** 404 when the category is not this customer's, or the shared category
       *  does not exist; 503 `not_recorded` means NOTHING was written. Also
       *  RE-POINTS the remembered name: the operator is stating what it means. */
      link: (tenantId: string, categoryId: string, nodeId: string) =>
        request<AdminCategoryLinkRow>(
          'POST',
          `/api/v1/admin/category-links/${categoryId}?tenant_id=${tenantId}`,
          { node_id: nodeId },
        ),
      /** 409 `not_linked` when it was not linked to begin with; 503
       *  `not_recorded` means NOTHING was written. */
      unlink: (tenantId: string, categoryId: string) =>
        request<AdminCategoryLinkRow>(
          'DELETE',
          `/api/v1/admin/category-links/${categoryId}?tenant_id=${tenantId}`,
        ),
    },
    // Deciding filter keys for a SHARED category (backend/admin/node_filters_api.py),
    // not per customer: one decision here changes the filter bar of every
    // customer whose category hangs on this one. Picking the shared category
    // itself reuses `adminOps.categoryLinks.nodes()` above -- the registry is
    // the same list, this is a different thing to do with it.
    //
    // Both writes 503 with `not_recorded` (nothing written, could not be
    // recorded) or `cannot_read_customers` (this connection may not read
    // across customers, so nothing changed) -- see the module docstring for
    // why those are the only two, and never a generic failure.
    categoryNodes: {
      filters: (nodeId: string) =>
        request<AdminNodeFiltersResponse>('GET', `/api/v1/admin/category-nodes/${nodeId}/filters`),
      /** `status` is `"active"` or `"rejected"` -- never a third value from
       *  here; the schema itself is a `Literal`. `reason` must be non-empty
       *  or the server answers 422. */
      decideKey: (nodeId: string, keySlug: string, status: 'active' | 'rejected', reason: string) =>
        request<{ status: string }>(
          'POST',
          `/api/v1/admin/category-nodes/${nodeId}/filters/${keySlug}/decision`,
          { status, reason },
        ),
      /** Approves the shared category as a whole -- the heaviest of the two
       *  writes, since it is what turns the filter bar on for every linked
       *  customer at once. */
      approve: (nodeId: string, reason: string) =>
        request<{ status: string }>(
          'POST',
          `/api/v1/admin/category-nodes/${nodeId}/approval`,
          { status: 'approved', reason },
        ),
    },
    // Whether the machine the filter agent runs on is alive and still accepts
    // our token (backend/admin/machine_api.py, task 3). Cached server-side for
    // five minutes — `cached` on the response says whether this reading is
    // that stored one or a fresh probe.
    agentVm: {
      status: () => request<AdminAgentVmStatus>('GET', '/api/v1/admin/agent-vm/status'),
    },
    // Fase-4 machine panels: is the engine still running (backend/admin/machine_api.py,
    // tasks 1-4). Shapes verified against machine_schemas.py and the route
    // signatures in machine_api.py directly, not the task-5a brief's sketch.
    machine: {
      crons: (days = 14) =>
        request<AdminCronsResponse>('GET', `/api/v1/admin/machine/crons?days=${days}`),
      jobs: (limit = 50) =>
        request<AdminJobsResponse>('GET', `/api/v1/admin/machine/jobs?limit=${limit}`),
      breakers: () => request<AdminBreakersResponse>('GET', '/api/v1/admin/machine/breakers'),
      resetAiBreaker: () =>
        request<AdminBreakerResetResult>('POST', '/api/v1/admin/machine/breakers/ai/reset'),
      resetRetailerAgentBreaker: (retailerSlug: string, country: string) =>
        request<AdminBreakerResetResult>('POST', '/api/v1/admin/machine/breakers/retailer/reset', {
          retailer_slug: retailerSlug,
          country,
        }),
      /** The EXISTING endpoint (backend/admin/router.py) for kind="retailer_scrape"
       *  rows — predates this panel and is untouched by it. Nothing in api.ts
       *  called it before this block; do not add a second call for it elsewhere. */
      resetRetailerScrapeBreaker: (retailerId: string) =>
        request<AdminRetailerScrapeBreakerReset>(
          'POST',
          `/api/v1/admin/retailers/${retailerId}/reset-circuit-breaker`,
        ),
      profiles: (limit = 50) =>
        request<AdminProfilesResponse>('GET', `/api/v1/admin/machine/profiles?limit=${limit}`),
    },
    // Task 5c: the read-only worker-liveness endpoint (backend/workers_api.py,
    // FR-H-2). Own top-level namespace, not nested under `machine`, because it
    // is a different backend router (`/api/v1/admin/workers`, not
    // `/api/v1/admin/machine/...`) — same split `agentVm` already has above.
    workers: {
      list: () => request<AdminWorkersResponse>('GET', '/api/v1/admin/workers'),
    },
    // Task 5c: agent_runs read endpoints (backend/agent_runs_api.py, FR-AG-1/2).
    agentRuns: {
      summary: () => request<AdminAgentRunsSummaryResponse>('GET', '/api/v1/admin/agent-runs/summary'),
      list: (params?: { component?: string; status?: string; limit?: number; offset?: number }) => {
        const qs = new URLSearchParams();
        if (params?.component) qs.set('component', params.component);
        if (params?.status) qs.set('status', params.status);
        if (params?.limit !== undefined) qs.set('limit', String(params.limit));
        if (params?.offset !== undefined) qs.set('offset', String(params.offset));
        const suffix = qs.toString() ? `?${qs}` : '';
        return request<AdminAgentRunsListResponse>('GET', `/api/v1/admin/agent-runs${suffix}`);
      },
    },
    // Task 5c: manual trigger + enable/disable (backend/agent_control_api.py,
    // FR-AG-2). These three routes sit behind an operator SESSION dependency
    // (Depends(get_current_user_id)) on top of the router-level operator
    // gate — an X-Admin-Token credential alone 401s here, unlike most of
    // `adminOps`. `request()` already sends the session cookie, so nothing
    // extra is needed here; this is only a note for whoever probes these by
    // hand and gets a confusing 401.
    agentControl: {
      flags: () => request<AdminAgentFlagsResponse>('GET', '/api/v1/admin/agent-flags'),
      setEnabled: (component: string, enabled: boolean) =>
        request<AdminAgentEnabledResult>('PATCH', `/api/v1/admin/agents/${component}/enabled`, { enabled }),
      /** 409 for any component not in the backend's TRIGGERABLE map — the
       *  caller must check that first (AdminAgentFlag alone doesn't say which
       *  components qualify; there is currently no endpoint that lists
       *  TRIGGERABLE itself, so AgentBrakePanel hardcodes the one member it
       *  knows about and still handles the 409 anyway). */
      trigger: (component: string) =>
        request<AdminAgentTriggerResult>('POST', `/api/v1/admin/agents/${component}/trigger`),
    },
    // Task 5c: classification pipeline health + KB bootstrap
    // (backend/admin/classification_health.py, backend/admin/classification_bootstrap.py).
    classification: {
      health: () => request<AdminClassificationHealthResponse>('GET', '/api/v1/admin/classification/health'),
      /** 202 — heavy work runs async via Arq. Poll `bootstrapStatus` for the
       *  outcome. Costs real money at external suppliers; confirm before
       *  calling this. */
      bootstrap: (categoryName: string, optionalHints?: string[]) =>
        request<AdminBootstrapResponse>('POST', '/api/v1/admin/classification/categories/bootstrap', {
          category_name: categoryName,
          optional_hints: optionalHints ?? null,
        }),
      bootstrapStatus: (jobId: string) =>
        request<AdminBootstrapStatusResponse>('GET', `/api/v1/admin/classification/categories/bootstrap/${jobId}`),
    },
  },
  dashboard: {
    summary: () => request<DashboardSummary>('GET', '/api/v1/dashboard/summary'),
    // V2 — single-category KPI block. Omit `category` to fall back to the
    // tenant's default_category. Backend response shape:
    // PortfolioSummaryResponse.
    portfolioSummary: (params?: {
      category?: string;
      brand?: string;
      freshness_hours?: number;
    }) => {
      const qs = new URLSearchParams();
      if (params?.category) qs.set('category', params.category);
      if (params?.brand) qs.set('brand', params.brand);
      if (params?.freshness_hours !== undefined)
        qs.set('freshness_hours', String(params.freshness_hours));
      const suffix = qs.toString() ? `?${qs}` : '';
      return request<PortfolioSummaryResponse>(
        'GET',
        `/api/v1/dashboard/portfolio-summary${suffix}`,
      );
    },
    // V2 — overview-modus: per-categorie tabel + totals.
    portfolioByCategory: (params?: { brand?: string; freshness_hours?: number }) => {
      const qs = new URLSearchParams({ group_by: 'category' });
      if (params?.brand) qs.set('brand', params.brand);
      if (params?.freshness_hours !== undefined)
        qs.set('freshness_hours', String(params.freshness_hours));
      return request<PortfolioByCategoryResponse>(
        'GET',
        `/api/v1/dashboard/portfolio-summary?${qs}`,
      );
    },
    // V2 — four outlier lists. Omit `category` for cross-categorie top-N
    // (each entry carries `category {slug, display_name}`).
    outliers: (params?: { category?: string; brand?: string; limit?: number }) => {
      const qs = new URLSearchParams();
      if (params?.category) qs.set('category', params.category);
      if (params?.brand) qs.set('brand', params.brand);
      if (params?.limit !== undefined) qs.set('limit', String(params.limit));
      const suffix = qs.toString() ? `?${qs}` : '';
      return request<OutliersResponse>('GET', `/api/v1/dashboard/outliers${suffix}`);
    },
  },
  agents: {
    listAll: () => request<AgentPromptListResponse>('GET', '/api/v1/admin/agents/'),
    get: (appSlug: string, name: string) =>
      request<AgentPromptResponse>('GET', `/api/v1/admin/agents/${appSlug}/${name}`),
    versions: (appSlug: string, name: string) =>
      request<AgentPromptVersionsResponse>(
        'GET',
        `/api/v1/admin/agents/${appSlug}/${name}/versions`,
      ),
    update: (appSlug: string, name: string, content: string) =>
      request<AgentPromptResponse>('PUT', `/api/v1/admin/agents/${appSlug}/${name}`, {
        content,
      }),
    rollback: (appSlug: string, name: string, version: number) =>
      request<AgentPromptResponse>(
        'POST',
        `/api/v1/admin/agents/${appSlug}/${name}/rollback`,
        { version },
      ),
  },
  notifications: {
    /** The signed-in user's own preference — never anyone else's. */
    get: () => request<NotificationPreference>('GET', '/api/v1/notifications/me'),
    update: (digest_mode: DigestMode) =>
      request<NotificationPreference>('PATCH', '/api/v1/notifications/me', { digest_mode }),
  },
  webhooks: {
    list: () => request<WebhookEndpointListResponse>('GET', '/api/v1/webhooks'),
    create: (payload: WebhookEndpointCreate) =>
      request<WebhookEndpointWithSecret>('POST', '/api/v1/webhooks', payload),
    update: (id: string, payload: WebhookEndpointUpdate) =>
      request<WebhookEndpoint>('PATCH', `/api/v1/webhooks/${id}`, payload),
    delete: (id: string) => request<void>('DELETE', `/api/v1/webhooks/${id}`),
    deliveries: (params?: { endpoint_id?: string; status?: WebhookDeliveryStatus; limit?: number; offset?: number }) => {
      const qs = new URLSearchParams();
      if (params?.endpoint_id) qs.set('endpoint_id', params.endpoint_id);
      if (params?.status) qs.set('status', params.status);
      if (params?.limit !== undefined) qs.set('limit', String(params.limit));
      if (params?.offset !== undefined) qs.set('offset', String(params.offset));
      const suffix = qs.toString() ? `?${qs}` : '';
      return request<WebhookDeliveryListResponse>('GET', `/api/v1/webhooks/deliveries${suffix}`);
    },
  },
  onboarding: {
    /** Kijk wat een winkel-adres oplevert vóór het wordt toegevoegd: land,
     *  interne naam, en of we de site konden openen. Kost geen scrape-credits. */
    preflight: (url: string) =>
      request<RetailerPreflight>('POST', '/api/v1/onboarding/preflight', { url }),
    start: (body: OnboardRetailerRequest) =>
      request<OnboardRetailerResponse>('POST', '/api/v1/onboarding/retailer', body),
    events: (jobId: string, since: number) => {
      const params = new URLSearchParams({ since: String(since) });
      return request<JobEventsResponse>(
        'GET',
        `/api/v1/onboarding/jobs/${jobId}/events?${params}`,
      );
    },
  },
  integrations: {
    status: () => request<IntegrationStatusResponse>('GET', '/api/v1/integrations/status'),
    // shopify/woocommerce → { authorize_url }; magento → { instructions }.
    connect: (platform: IntegrationPlatform, shopHint: string) =>
      request<IntegrationConnectResponse>('POST', `/api/v1/integrations/${platform}/connect`, {
        shop_hint: shopHint,
      }),
    // Free-form credential body (platform-specific fields). See WebshopCard.
    manualConnect: (platform: IntegrationPlatform, body: Record<string, string>) =>
      request<IntegrationManualResponse>('POST', `/api/v1/integrations/${platform}/manual`, body),
    disconnect: (platform: IntegrationPlatform, credentialId: string) =>
      request<{ ok: boolean }>('POST', `/api/v1/integrations/${platform}/disconnect`, {
        credential_id: credentialId,
      }),
    // Layer-2 opt-in catalog selection.
    previewCatalog: (platform: IntegrationPlatform, body: CatalogPreviewRequest) =>
      request<CatalogPreviewResponse>(
        'POST', `/api/v1/integrations/${platform}/catalog/preview`, body,
      ),
    confirmCatalog: (platform: IntegrationPlatform, body: CatalogConfirmRequest) =>
      request<CatalogConfirmResponse>(
        'POST', `/api/v1/integrations/${platform}/catalog/confirm`, body,
      ),
  },
  pricing: {
    generate: (country: string) =>
      request<GenerateResult>('POST', '/api/v1/pricing/suggestions/generate', { country }),
    policy: () => request<PricingPolicy>('GET', '/api/v1/pricing/policy'),
    previewRule: (payload: RulePreviewRequest) =>
      request<RulePreview>('POST', '/api/v1/pricing/rules/preview', payload),
    setPolicy: (payload: PricingPolicy) =>
      request<PricingPolicy>('PUT', '/api/v1/pricing/policy', payload),
    // `seenPrices` is what the screen showed per proposal. A proposal the
    // nightly run rewrote keeps its id, so without it the customer approves a
    // price he never saw. `pending` withdraws an approval that has not gone out.
    decide: (
      ids: string[],
      decision: 'approved' | 'rejected' | 'pending',
      seenPrices?: Record<string, number>,
    ) =>
      request<{ changed: number }>('POST', '/api/v1/pricing/suggestions/decide', {
        ids,
        decision,
        seen_prices: seenPrices,
      }),
    push: (credentialId: string, country: string, opts?: PushOptions) =>
      request<{ status: string }>('POST', '/api/v1/pricing/push', {
        credential_id: credentialId, country, ...opts,
      }),
    pushPreview: (credentialId: string, country: string) =>
      request<PushPreview>(
        'GET',
        `/api/v1/pricing/push/preview?credential_id=${encodeURIComponent(credentialId)}&country=${country}`,
      ),
    pushLog: (limit = 100) =>
      request<PushLogEntry[]>('GET', `/api/v1/pricing/push-log?limit=${limit}`),
    healthEvents: (limit = 50) =>
      request<DataHealthEvent[]>('GET', `/api/v1/pricing/health-events?limit=${limit}`),
    rules: () => request<PricingRule[]>('GET', '/api/v1/pricing/rules'),
    createRule: (body: {
      name: string; rule_type: string;
      params?: Record<string, unknown>; scope?: Record<string, unknown>; priority?: number;
    }) => request<PricingRule>('POST', '/api/v1/pricing/rules', body),
    updateRule: (id: string, body: Partial<{
      name: string; params: Record<string, unknown>; scope: Record<string, unknown>;
      priority: number; enabled: boolean;
    }>) => request<PricingRule>('PATCH', `/api/v1/pricing/rules/${id}`, body),
    deleteRule: (id: string) => request<void>('DELETE', `/api/v1/pricing/rules/${id}`),
    /** Automatic sending for one rule. On needs `confirm` = the rule's name;
     *  otherwise 409 `shadow_period` | `stopped` | `confirm_required`. */
    setAutoPush: (id: string, on: boolean, confirm?: string) =>
      request<PricingRule>('POST', `/api/v1/pricing/rules/${id}/auto-push`, { on, confirm }),
    retry: (ids: string[]) =>
      request<{ changed: number }>('POST', '/api/v1/pricing/suggestions/retry', { ids }),
    readiness: (country = 'NL') =>
      request<PricingReadiness>('GET', `/api/v1/pricing/readiness?country=${country}`),
    automation: () => request<AutomationState>('GET', '/api/v1/pricing/automation'),
    shadow: (days = 30) => request<ShadowReport>('GET', `/api/v1/pricing/shadow?days=${days}`),
    rollbackPreview: (country = 'NL') =>
      request<RollbackPreview>('GET', `/api/v1/pricing/rollback/preview?country=${country}`),
    /** The last round from the log alone (no shop read); null when none. */
    roundsLast: (country: string) =>
      request<LastRound | null>('GET', `/api/v1/pricing/rounds/last?country=${country}`),
    /** Undo the round the preview named; 409 `round_changed` when another
     *  round became the last one since. */
    rollback: (country: string, batchId: string) =>
      request<RollbackResult>(
        'POST',
        `/api/v1/pricing/rollback?country=${country}&batch_id=${encodeURIComponent(batchId)}`,
      ),
    setAutomationStop: (stopped: boolean, reason?: string) =>
      request<AutomationState>('POST', '/api/v1/pricing/automation/stop', { stopped, reason }),
    strategy: (country = 'NL') =>
      request<StrategyOverview>('GET', `/api/v1/pricing/strategy?country=${country}`),
    review: {
      summary: (country: string, category?: string) => {
        const qs = new URLSearchParams({ country });
        if (category !== undefined) qs.set('category', category);
        return request<PricingReviewSummary>('GET', `/api/v1/pricing/review/summary?${qs}`);
      },
      items: (params: PricingReviewItemsParams) =>
        request<{ total: number; items: PricingReviewItem[] }>(
          'GET', `/api/v1/pricing/review/items?${reviewQuery(params)}`,
        ),
      /** Facet sidebar counts over every matching row (not one page). Each
       *  key ignores its own selection; `total` ignores the whole selection. */
      facetCounts: (params: Omit<PricingReviewItemsParams, 'sort' | 'limit' | 'offset'>) =>
        request<{ counts: Record<string, Record<string, number>>; total: number }>(
          'GET', `/api/v1/pricing/review/facet-counts?${reviewQuery(params)}`,
        ),
      sample: (country: string, category?: string, size = 10) => {
        const qs = new URLSearchParams({ country, size: String(size) });
        if (category !== undefined) qs.set('category', category);
        return request<{ items: PricingReviewItem[] }>('GET', `/api/v1/pricing/review/sample?${qs}`);
      },
      decide: (body: PricingReviewDecide) =>
        request<{ changed: number }>('POST', '/api/v1/pricing/review/decide', body),
    },
  },
};

export interface AgentPromptResponse {
  id: string;
  app_slug: string;
  name: string;
  version: number;
  content: string;
  active: boolean;
  updated_at: string;
  updated_by: string | null;
}

export interface AgentPromptSummary {
  app_slug: string;
  name: string;
  version: number;
  active: boolean;
  updated_at: string;
  updated_by: string | null;
  content_chars: number;
}

export interface AgentPromptListResponse {
  items: AgentPromptSummary[];
  total: number;
}

export interface AgentPromptVersionsResponse {
  app_slug: string;
  name: string;
  versions: AgentPromptResponse[];
}

export type WebhookEventType =
  | 'alert.triggered'
  | 'sku.added'
  | 'sku.archived'
  | 'msrp.changed'
  | 'run.completed'
  | 'catalog.sync.completed';

export type WebhookDeliveryStatus = 'pending' | 'in_progress' | 'sent' | 'failed';

export interface WebhookEndpoint {
  id: string;
  url: string;
  description: string | null;
  event_types: WebhookEventType[];
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface WebhookEndpointWithSecret extends WebhookEndpoint {
  secret: string;
}

export interface WebhookEndpointListResponse {
  items: WebhookEndpoint[];
  total: number;
}

export interface WebhookEndpointCreate {
  url: string;
  description?: string | null;
  event_types: WebhookEventType[];
  active?: boolean;
}

export interface WebhookEndpointUpdate {
  description?: string | null;
  event_types?: WebhookEventType[];
  active?: boolean;
}

export interface WebhookDelivery {
  id: string;
  endpoint_id: string;
  event_type: string;
  status: WebhookDeliveryStatus;
  attempt_count: number;
  idempotency_key: string;
  created_at: string;
  last_attempt_at: string | null;
  next_attempt_at: string | null;
  response_status: number | null;
  error_message: string | null;
  payload: Record<string, unknown>;
}

export interface WebhookDeliveryListResponse {
  items: WebhookDelivery[];
  total: number;
}

export interface AdminDbHealth {
  db_version: string;
  timescaledb_version: string | null;
  connection_count: number;
  max_connections: number;
  db_size_bytes: number;
  db_size_human: string;
}

export interface AdminTableStat {
  table: string;
  row_count: number;
  total_size_bytes: number;
  total_size_human: string;
}

export interface AdminTenantStat {
  tenant_id: string;
  slug: string;
  name: string;
  sku_count: number;
  listing_count: number;
  open_alert_count: number;
}

export interface AdminHealthResponse {
  db: AdminDbHealth;
  tables: AdminTableStat[];
  tenants: AdminTenantStat[];
  queue_depth: number;
}

// -- adminOps: the operator customer console (backend/admin/*). Separate from
// AdminHealthResponse/AdminSpendResponse above, which predate this and only
// cover platform health + AI spend -- see the `adminOps` block below for why
// these are not merged into `admin`.
//
// Shapes verified against backend/admin/tenant_schemas.py, tenant_ops.py,
// tenants_api.py, users_api.py and signup/router.py directly, not from a
// sketch: several fields (breaker_state, last_catalog_sync, credits_window_days)
// were added to the backend after the original design and are easy to miss.

/** GET /api/v1/admin/summary — backend/admin/summary_api.py::AdminSummary.
 *  `breakers_open` is null when Redis or the DB could not be read: show
 *  nothing, never a zero. */
export type AdminSummary = {
  problems_open: number;
  support_open: number;
  support_waiting: number;
  signups_pending: number;
  breakers_open: number | null;
  categories_unlinked: number;
  customers_total: number;
};

export type AdminTenantRow = {
  tenant_id: string;
  slug: string;
  name: string;
  client_type: string;
  status: string;
  home_country: string | null;
  plan: string | null;
  trial_ends_at: string | null;
  // True for exactly the one customer named by the backend's
  // platform_tenant_slug setting -- never guess that slug on the frontend
  // (SupportTicketPage.tsx used to hardcode 'outflanq', which is wrong on
  // any stack where the setting differs, dev included).
  is_platform: boolean;
  user_count: number;
  sku_count: number;
  retailer_count: number;
  open_ticket_count: number;
  last_scrape_at: string | null;
  last_login_at: string | null;
  /** ISO 8601. Used by the Vandaag page for "trial without products". */
  created_at: string | null;
  // The trial funnel (#648): null = step not reached. "Confirmed" is
  // created_at. Optional only so older fixtures still type-check.
  signed_up_at?: string | null;
  first_product_at?: string | null;
  first_competitor_at?: string | null;
  first_measured_at?: string | null;
};

// -- adminOps.tenants.entitlements: backend/admin/entitlements_api.py.
// The three resources always arrive in this order (skus, retailers, users) and
// the screen does not re-sort them.

export type AdminEntitlementLimit = {
  resource: string;
  used: number;
  limit: number;
  // 'plan' = the ceiling is the plan's. 'override' = an exception in this
  // customer's flags. An override that cannot be read as a number counts as
  // 'plan', because then the number shown really IS the plan's.
  source: 'plan' | 'override';
};

export type AdminEntitlements = {
  // How the plan RESOLVES -- a misspelled stored plan falls back to the
  // smallest one, and `features.plan` is what literally stands there.
  plan: string;
  limits: AdminEntitlementLimit[];
  features: Record<string, string>;
};

export type AdminTenantDetail = AdminTenantRow & {
  created_at: string | null;
  features: Record<string, string>;
  internal_notes: string | null;
  // WHY this customer is suspended, and the two answers need opposite buttons:
  // null means the nightly sweep suspended him on a lapsed trial (a new end
  // date lets him back in), 'cancelled' means the subscription ended (only the
  // status button does). null as well on any customer who is not suspended.
  suspended_reason: string | null;
};

/** What POST /api/v1/admin/tenants/{id}/impersonate answers.
 *  `expires_at` is read back out of the minted token itself, so the band's
 *  clock and the token's clock are the same clock. */
export type ImpersonationGrant = {
  token: string;
  expires_at: string;
  tenant_name: string;
};

export type AdminUserRow = {
  user_id: string;
  email: string;
  name: string;
  role: Role;
  is_active: boolean;
  is_pending: boolean;
  last_login_at: string | null;
};

// backend/admin/users_api.py::UserLockout. `locked_until` is a UTC ISO
// timestamp and is null exactly when `locked` is false. `failed_attempts`
// counts inside the sliding window, so it is readable before the ceiling is
// reached -- four out of five is worth seeing.
export type AdminUserLockout = {
  locked: boolean;
  failed_attempts: number;
  locked_until: string | null;
};

export type AdminTenantOpsRetailer = {
  retailer_id: string;
  slug: string;
  active: boolean;
  provider: string | null;
  frequency_per_day: number | null;
  last_run_at: string | null;
  last_status: string | null;
  last_listings: number | null;
  last_credits: number | null;
  breaker_state: string | null;
};

export type AdminTenantOpsRun = {
  started_at: string;
  retailer_slug: string | null;
  duration_seconds: number | null;
  listings_count: number | null;
  credits_used: number | null;
  status: string;
};

export type AdminTenantOpsCatalogSync = {
  state: string;
  started_at: string | null;
  completed_at: string | null;
  received: number | null;
  created_count: number | null;
  updated_count: number | null;
  failed_count: number | null;
  orphaned_count: number | null;
};

export type AdminTenantOps = {
  retailers: AdminTenantOpsRetailer[];
  runs: AdminTenantOpsRun[];
  mapping: { sku_total: number; sku_mapped: number; sku_unmapped: number; review_queue: number };
  credits: { provider: string; calls: number; credits: number }[];
  /** The window (in days) the `credits` figures cover -- read the label from
   *  this instead of hardcoding a period that could drift from the backend's. */
  credits_window_days: number;
  last_catalog_sync: AdminTenantOpsCatalogSync | null;
};

export type AdminAuditRow = {
  created_at: string;
  action: string;
  resource_type: string;
  resource_id: string | null;
  actor_user_id: string | null;
  actor_email: string | null;
  actor_type: string | null;
  result: string | null;
};

export type AdminSignup = {
  id: string;
  company: string;
  name: string;
  email: string;
  note: string | null;
  created_at: string;
  approved_at: string | null;
  rejected_at: string | null;
  tenant_id: string | null;
};

export type AdminProblemSeverity = 'high' | 'medium' | 'low';

export interface AdminLadderRow {
  slug: string;
  country: string;
  /** Residential attempts try a browser session (cookies + UA) first. */
  browser: boolean;
  ladder_stats: Record<string, { ok?: number; blocked?: number; empty?: number }> | null;
  bytes_used: number | null;
  cost_micros: number | null;
  last_round_at: string | null;
  success_pct: number | null;
}

/** Shape verified against backend/admin/problems_api.py::CustomerProblemRow. */
export interface AdminCustomerProblem {
  id: string;
  tenant_id: string;
  tenant_name: string;
  kind: string;
  subject_key: string;
  severity: AdminProblemSeverity;
  detail: string;
  first_seen_at: string;
  last_seen_at: string;
  occurrences: number;
  resolved_at: string | null;
  resolved_by: string | null;
}

export type AdminNewTenant = {
  name: string;
  slug?: string;
  client_type: string;
  home_country?: string;
  admin_email: string;
  admin_name: string;
  plan?: string;
};

export type ClientType = 'brand' | 'distributor' | 'etailer';
export type TenantStatus = 'active' | 'archived' | 'deleted';

// -- adminOps.support: the operator support inbox (backend/admin/support_*.py,
// backend/support/*.py). `direction` is 'in' | 'out' | 'note'; `status` is
// 'open' | 'waiting' | 'closed'. Attachments carry only name + size -- the
// mailbox never stores the file itself (backend/support/intake.py).

export type AdminSupportTicketStatus = 'open' | 'waiting' | 'closed';

export type AdminSupportTicketRow = {
  ticket_id: string;
  ticket_no: number;
  tenant_id: string | null;
  tenant_name: string | null;
  from_email: string;
  from_name: string | null;
  subject: string;
  status: string;
  assignee_user_id: string | null;
  assignee_email: string | null;
  created_at: string;
  last_message_at: string;
  message_count: number;
};

export type AdminSupportAttachment = {
  name: string;
  size: number;
};

export type AdminSupportMessage = {
  message_row_id: string;
  direction: string;
  from_email: string | null;
  to_email: string | null;
  subject: string | null;
  body_text: string;
  message_id: string | null;
  in_reply_to: string | null;
  author_email: string | null;
  attachments: AdminSupportAttachment[];
  created_at: string;
};

export type AdminSupportTicketDetail = AdminSupportTicketRow & {
  messages: AdminSupportMessage[];
  tenant_slug: string | null;
  internal_notes: string | null;
};

// -- adminOps.webhooks: backend/admin/webhooks_schemas.py, one-for-one.
// `WebhookEndpointRow` carries no secret field today, and the backend model
// deliberately has none either (its own docstring says why). That is a fact
// about the current shape, not a guarantee about the future -- nothing here
// enforces it, so re-check it against webhooks_schemas.py rather than
// trusting this comment.
// `url` IS shown in full: at Slack, Zapier and Make the endpoint URL is
// itself a credential, and it is shown anyway because an operator otherwise
// cannot see WHICH endpoint is failing (see the comment on that field in
// webhooks_schemas.py). The agreement that goes with that -- never logged,
// never exported, this response never widened to a caller without an
// operator credential -- is an agreement between people, not something the
// code checks.

export type AdminWebhookEndpointRow = {
  endpoint_id: string;
  url: string;
  description: string | null;
  event_types: string[];
  active: boolean;
  created_at: string | null;
};

export type AdminWebhookDeliveryRow = {
  delivery_id: string;
  endpoint_id: string;
  url: string;
  event_type: string;
  status: string;
  attempt_count: number;
  /** null = the far end never answered. Unknown, NOT zero. */
  response_status: number | null;
  error_message: string | null;
  last_attempt_at: string | null;
  next_attempt_at: string | null;
  created_at: string;
};

export type AdminWebhooksResponse = {
  endpoints: AdminWebhookEndpointRow[];
  recent: AdminWebhookDeliveryRow[];
  /** Counted WITHOUT the limit, so a cut-off list can say it is cut off. */
  recent_total: number;
  dead_letter: AdminWebhookDeliveryRow[];
  dead_letter_total: number;
};

// -- adminOps.categoryLinks: backend/admin/category_links_schemas.py,
// one-for-one. Every id is a string there on purpose (it says so): these go
// straight into a URL and a query key.
//
// `node_*`, `source`, `confidence` and `linked_at` are null TOGETHER -- that
// is what an unlinked row is, and it is the row an operator came for.
// `confidence` is a real number only for an `ai` link; `alias` carries 100
// (deterministic) and a manual link carries none, because a percentage next
// to a person's decision would read as a machine's certainty about it.

// -- adminOps.facets.valueGroups / .assignValueGroup:
// backend/admin/facet_value_groups_api.py, one-for-one (issue #480).
//
// `source` is a plain string, not a union of two literals: it is written by
// the server and a third provenance landing here must still render a row.
// "manual" means a person said something about this spelling -- including
// when he said the same thing the model did, which is different information
// from "the model guessed this".
export type AdminValueGroupMember = {
  value: string;
  /** How many of this customer's active products carry this exact spelling.
   *  Zero is real: a spelling can survive in the mapping after the last
   *  product carrying it is gone. */
  product_count: number;
  /** "model" | "manual" */
  source: string;
};

export type AdminValueGroup = {
  /** The one choice the filter bar shows for every member below. */
  canonical: string;
  members: AdminValueGroupMember[];
  /** The sum over the members -- how many products this one choice covers. */
  product_count: number;
};

export type AdminValueGroupKey = {
  key_slug: string;
  label: string;
  groups: AdminValueGroup[];
};

export type AdminValueGroupsResponse = {
  keys: AdminValueGroupKey[];
};

export type AdminCategoryLinkRow = {
  category_id: string;
  tenant_id: string;
  customer_name: string;
  category_name: string;
  node_id: string | null;
  node_slug: string | null;
  node_name: string | null;
  /** "alias" | "ai" | "manual" | null -- null exactly when node_id is null. */
  source: string | null;
  confidence: number | null;
  /** ISO-8601, or null on an unlinked row. */
  linked_at: string | null;
};

// -- adminOps.categoryLinks.nodes: backend/admin/category_links_schemas.py
// (CategoryNodeRow). The two counts are what makes a choice between two
// similar-reading shared categories possible.
export type AdminCategoryNode = {
  id: string;
  slug: string;
  display_name: string;
  /** How many spellings are already known to mean this one. */
  alias_count: number;
  /** How many customer categories point at it. */
  linked_categories: number;
};

export type AdminCategoryLinksResponse = {
  items: AdminCategoryLinkRow[];
  /** Counted WITHOUT the limit, so a cut-off list can say it is cut off. */
  total: number;
  unlinked_total: number;
  /** False = this connection's role cannot read past RLS, so an empty list
   *  means "not allowed to look", not "nothing to link" (issue #364). A
   *  caller that renders that list without saying so ships the exact bug
   *  this track exists to remove. */
  can_read_across_customers: boolean;
};

// -- adminOps.categoryNodes: backend/admin/node_filters_schemas.py, one-for-one.
// `status` and `approval_status` are plain strings there ON PURPOSE, not a
// union: this screen shows candidates and rejected keys too, and a fourth
// value that lands in the CHECK constraint before this type is updated must
// still render a row instead of crashing the page.
export type AdminNodeFilterKeyRow = {
  key_slug: string;
  label: string;
  /** "candidate" | "active" | "rejected" -- see the note above. */
  status: string;
  /** A COUNT of customers who proposed this key, never which ones -- the
   *  shared library never names a customer back to an operator. */
  contributed_by: number;
};

export type AdminNodeFiltersResponse = {
  node_name: string;
  /** The number this screen exists to show before any button works: how many
   *  customers' filter bars a decision here touches. */
  affected_customers: number;
  /** "proposed" | "approved" | "rejected" -- plain string, same reasoning as
   *  `AdminNodeFilterKeyRow.status`. */
  approval_status: string;
  keys: AdminNodeFilterKeyRow[];
};

// -- adminOps.agentVm: whether the filter agent's machine is alive and our
// token still works (backend/admin/machine_api.py). Shape verified against
// that file directly, not the task-4 brief's sketch. Two catches, both
// explained at length in machine_api.py:
//  - `queued_research_jobs` was `running_research_jobs` in the original
//    design (zero queued does not mean zero running — Arq drops a job from
//    the queue set the moment a worker picks it up).
//  - `slots_total`/`slots_busy` are GONE (blocker found in fase-3 final
//    review): they read a semaphore living in the web process answering this
//    HTTP request, but the actual VM work runs in the Arq worker — a
//    different container with its own, separate semaphore. The web process
//    always reported 0 busy, even with the worker holding every slot.
//    Replaced by `max_concurrency` (config — the ceiling, not an
//    observation) and `running_research_jobs` (a live Redis scan for
//    `arq:in-progress:facets_*`, which works across containers).
export interface AdminAgentVmStatus {
  reachable: boolean;
  token_valid: boolean;
  /** The concurrency CEILING this backend will ever allow — configuration,
   *  not a reading. Never call this "busy"; `running_research_jobs` is the
   *  live count. */
  max_concurrency: number;
  /** Research jobs a worker is ACTUALLY executing right now. */
  running_research_jobs: number;
  /** Research jobs WAITING in the queue, not yet picked up. */
  queued_research_jobs: number;
  /** ISO timestamp of the real measurement — stays the ORIGINAL time on a
   *  cached answer, never "now". */
  checked_at: string;
  /** True when this answer came from the five-minute cache, not a fresh probe. */
  cached: boolean;
  /** The kind of failure, e.g. 'not_configured' | 'vm_rejected_token' | an
   *  exception class name — never the token, never a URL carrying it. */
  error: string | null;
}

// -- adminOps.machine (fase-4): the four panels answering "is the engine
// still running" — daily crons, the Arq job queue, circuit breakers, and
// retailer profiles awaiting review. Shapes copied verbatim from
// backend/admin/machine_schemas.py, not from the task-5a brief's sketch.
// Every "unreachable" flag below must render a visible sentence in its
// panel — an empty list with no flag beside it is the exact failure mode
// four backend tasks were spent removing; see machine_schemas.py for the
// full reasoning behind each field.

/** GET /api/v1/admin/machine/crons?days=14 (days: 1..14, 422 outside that) */
export interface AdminCronDay {
  day: string; // ISO date
  /** A marker exists for that day = the job STARTED. It does NOT mean it
   *  finished. The marker is written before the work, deliberately, so a
   *  run cancelled half-way counts as a partial day and not as a free
   *  retry. Never label this "gelukt" / "succeeded" on screen. */
  ran: boolean;
}

export interface AdminCronRow {
  name: string;
  schedule_utc: string; // "06:30"
  last_run_day: string | null; // ran, not succeeded -- see AdminCronDay.ran
  missed_days: string[];
  history: AdminCronDay[]; // oldest first, draws left-to-right as a strip
}

export interface AdminCronsResponse {
  window_days: number;
  redis_reachable: boolean;
  /** False = the list of daily jobs itself could not be read. An empty
   *  `crons` then means "unknown", not "none" — the panel must say so. */
  registry_available: boolean;
  crons: AdminCronRow[];
}

/** GET /api/v1/admin/machine/jobs?limit=50 (limit: 1..200) */
export interface AdminFailedJob {
  job_id: string;
  function: string;
  enqueue_time: string | null;
  finish_time: string | null;
  error: string; // exception kind + first line only, never a stacktrace
  tries: number;
}

export interface AdminJobsResponse {
  redis_reachable: boolean;
  /** null = could not be read. NOT the same as 0. */
  queue_depth: number | null;
  failed: AdminFailedJob[];
  failed_total: number; // can exceed failed.length because of the `limit` param
}

/** GET /api/v1/admin/machine/breakers */
export interface AdminBreakerRow {
  kind: 'retailer_agent' | 'retailer_scrape' | 'ai';
  label: string;
  state: string; // "tripped" | "degraded" | "dead"
  opened_at: string | null; // null for both retailer kinds -- no honest value exists
  retry_after: string | null;
  failure_count: number | null;
  /** Filled ONLY for kind="retailer_scrape". It is the id the EXISTING
   *  reset endpoint takes. A retailer_agent row has no id — its reset takes
   *  slug+country. */
  retailer_id: string | null;
  retailer_slug: string | null;
  country: string | null;
  tenant_id: string | null; // retailer_scrape only; that row is per customer
  /** The customer's NAME, for retailer_scrape rows. Null when it could not be
   *  read (`tenants` is FORCE RLS). Show the name; fall back to the id only so
   *  the row still identifies something. */
  tenant_name: string | null;
}

export interface AdminBreakersResponse {
  redis_reachable: boolean;
  db_reachable: boolean;
  /** Issue #364. False means the operator database connection is NOT
   *  allowed to read across customers, and then every cross-customer list
   *  on this console is silently empty rather than wrong. Must be shown,
   *  loudly, when false. */
  db_can_read_across_customers: boolean;
  breakers: AdminBreakerRow[];
}

/** POST /api/v1/admin/machine/breakers/ai/reset and
 *  POST /api/v1/admin/machine/breakers/retailer/reset. A 200 is not a
 *  success: `ok: false` is a real, expected outcome — never treat any 200
 *  as "done" without reading `ok`. */
export interface AdminBreakerResetResult {
  ok: boolean;
  detail?: string;
}

/** POST /api/v1/admin/retailers/{retailer_id}/reset-circuit-breaker — the
 *  EXISTING endpoint (backend/admin/router.py) for kind="retailer_scrape"
 *  rows only. Different shape from AdminBreakerResetResult above: this one
 *  predates the machine panels and always answers `ok` + `new_state`, never
 *  `detail`. */
export interface AdminRetailerScrapeBreakerReset {
  ok: boolean;
  retailer_id: string;
  new_state: string;
}

/** GET /api/v1/admin/machine/profiles?limit=50 (limit: 1..200) */
export interface AdminProfileRow {
  retailer_slug: string;
  country_code: string;
  status: string; // 'auto_live' | 'pending_review' | 'unsupported_v1' | 'degraded' | 'needs_catalog'
  confidence: number | null;
  created_at: string | null; // there is no updated_at on this table
  degraded_reason: string | null;
}

export interface AdminPromotionRow {
  retailer_slug: string;
  at: string;
  component: string; // 'onboarding' | 'matching'
  disposition: string;
  hit_rate: number | null;
  sample_size: number | null;
}

export interface AdminProfilesResponse {
  db_reachable: boolean;
  pending_review: AdminProfileRow[];
  /** Counted WITHOUT the limit. Show "de eerste 50 van 60" whenever the
   *  total exceeds the list length — same treatment as `failed_total`. */
  pending_review_total: number;
  recent_promotions: AdminPromotionRow[];
  recent_promotions_total: number;
}

// -- Task 5c: five endpoints nothing in the frontend has ever called before
// this task — there was not a single TypeScript type for any of them. Shapes
// verified directly against backend/workers_api.py, backend/agent_runs_api.py,
// backend/agent_control_api.py and backend/admin/classification_health.py
// (the classification-health shape was additionally checked by exercising
// the real Pydantic models offline — see task-5c-report.md — because its
// top-level JSON key, `global`, is produced by a Field alias that a source
// read alone cannot fully rule out being lost somewhere in serialization).

/** GET /api/v1/admin/workers (backend/workers_api.py). `age_seconds` is
 *  computed server-side off a single DB clock (skew-proof) — never derive it
 *  client-side from `last_seen` and the browser's own clock. */
export interface AdminWorkerHeartbeat {
  worker_name: string;
  last_seen: string; // ISO
  age_seconds: number;
}

export interface AdminWorkersResponse {
  workers: AdminWorkerHeartbeat[];
}

/** GET /api/v1/admin/agent-runs/summary (backend/agent_runs_api.py) — latest
 *  run per component. `status` is nullable in the Pydantic model; treat null
 *  as unknown, never as success. */
export interface AdminAgentRunSummary {
  component: string;
  status: string | null;
  started_at: string | null;
  finished_at: string | null;
  error_class: string | null;
  /** Both columns are NOT NULL DEFAULT 0 on `agent_runs`, and the real
   *  numbers are written when the run FINISHES — so a 0 on a run still in
   *  flight means "not recorded yet", not "cost nothing". The panel only
   *  shows them for a finished run. `serper_credits` / `scrape_do_credits`
   *  exist on that table too and are deliberately never sent: those column
   *  names carry supplier names. */
  cost_usd: number | null;
  scrape_credits: number | null;
}

export interface AdminAgentRunsSummaryResponse {
  items: AdminAgentRunSummary[];
}

/** GET /api/v1/admin/agent-runs (same file) — paginated run history, newest
 *  first. `limit` is clamped server-side to 1..200. */
export interface AdminAgentRunItem {
  id: string;
  component: string;
  stage: string;
  status: string | null;
  started_at: string | null;
  finished_at: string | null;
  error_class: string | null;
  /** See AdminAgentRunSummary above. */
  cost_usd: number | null;
  scrape_credits: number | null;
}

export interface AdminAgentRunsListResponse {
  items: AdminAgentRunItem[];
}

/** GET /api/v1/admin/agent-flags (backend/agent_control_api.py). A component
 *  with no row in `agent_flags` is enabled by default and simply does not
 *  appear here — an empty `flags` array is a real, valid answer, not a
 *  failure. */
export interface AdminAgentFlag {
  component: string;
  enabled: boolean;
  updated_at: string | null;
  updated_by: string | null;
  /** The person behind `updated_by`, resolved server-side. Null when the lookup failed or the row is untouched — then the id is all there is.
   */
  updated_by_name: string | null;
}

export interface AdminAgentFlagsResponse {
  flags: AdminAgentFlag[];
}

/** PATCH /api/v1/admin/agents/{component}/enabled */
export interface AdminAgentEnabledResult {
  component: string;
  enabled: boolean;
}

/** POST /api/v1/admin/agents/{component}/trigger. 409 (not this promise
 *  rejecting quietly) when `component` is not in the backend's TRIGGERABLE
 *  map. */
export interface AdminAgentTriggerResult {
  job_id: string;
  enqueued_at: string;
}

/** GET /api/v1/admin/classification/health (backend/admin/classification_health.py). */
export interface AdminCategoryHealth {
  category: string;
  kb_version: string | null;
  last_updated: string | null;
  last_drift_check: string | null;
  drift_rate_last_check: number | null;
  auto_generated: boolean;
  needs_revision: boolean;
  classifications_24h: number;
  auto_changes_24h: number;
}

/** `scrape_credits_today` is the backend's field name — on screen this is
 *  "gebruik" / "kosten", the word "credits" never appears. */
export interface AdminGlobalClassificationHealth {
  ai_circuit_state: string;
  tailscale_mode_last_5min: string | null;
  claude_calls_today: number;
  scrape_credits_today: number;
}

/** The JSON key is `global` (a Python keyword, so the Pydantic field is
 *  `global_` with `Field(alias="global")`, and the endpoint dumps with
 *  `by_alias=True`). Confirmed against the endpoint's own Pydantic models
 *  offline, not assumed from reading the decorator — see the report. */
export interface AdminClassificationHealthResponse {
  global: AdminGlobalClassificationHealth;
  categories: AdminCategoryHealth[];
}

/** POST /api/v1/admin/classification/categories/bootstrap
 *  (backend/admin/classification_bootstrap.py) — 202, real paid work enqueued
 *  via Arq. */
export interface AdminBootstrapResponse {
  job_id: string;
  status: string; // 'queued'
}

/** The Arq job's return value once `arq_status` is `complete` — a
 *  `BootstrapResult` dataclass dumped via `__dict__`
 *  (agents/classification/bootstrap/category_bootstrap.py). `status` here is
 *  the OUTCOME of the paid work, not to be confused with `arq_status` above:
 *  a `complete` Arq job can still have REFUSED to build the KB. Two of the
 *  three values are refusals — never render any of them as a plain success. */
export interface AdminBootstrapResult {
  status: 'success' | 'aborted_insufficient_data' | 'aborted_claude_rejected';
  category_name: string;
  slug: string;
  commit_sha: string | null;
  reason: string | null;
  source_count: number;
  authoritative_count: number;
}

/** GET .../bootstrap/{job_id}. `not_found` is a real end state (the Arq
 *  result expired or the id is unknown) — not a reason to keep polling. */
export interface AdminBootstrapStatusResponse {
  job_id: string;
  arq_status: 'queued' | 'in_progress' | 'complete' | 'deferred' | 'not_found';
  result: AdminBootstrapResult | null;
}

/** What the customer may do about the add-on module (spec B20-B22). The
 *  server decides; the app only words it. */
export type MarketplaceInsightOffer =
  | 'switch_on' | 'switch_off' | 'included' | 'upgrade' | 'after_trial' | 'contact';

export interface MarketplaceInsightStatus {
  active: boolean;
  /** On through the subscription (false when we switched it on for him). */
  billed: boolean;
  offer: MarketplaceInsightOffer;
  /** What it costs on his own plan; null where it is not sold. */
  price_eur_month: number | null;
  plan_prices_eur_month: Record<'business' | 'professional', number>;
}

/** GET /api/v1/me/entitlements — the customer's own plan, ceilings and usage. */
export interface Entitlements {
  plan: CurrentPlan;
  limits: { skus: number; retailers: number; users: number };
  usage: { skus: number; retailers: number; users: number };
  features: { price_proposals: boolean; dynamic_pricing: boolean };
  scans_per_day: number;
  /** ISO timestamp while a free trial runs, null for a paying customer. */
  trial_ends_at: string | null;
  /** Optional: an older backend (and older test mocks) do not send it. */
  marketplace_insight?: MarketplaceInsightStatus;
  /** The plans he may pick himself, served from the same table the backend
   *  enforces, so the app can never offer a ceiling or price it disagrees
   *  with. Enterprise is negotiated and is never in this list. */
  self_service_plans: PlanOption[];
}

export type LegalDocSlug = 'av' | 'privacy' | 'vwo' | 'subverwerkers';

export interface LegalDocument {
  doc: LegalDocSlug;
  language: Locale;
  /** False when the reader is looking at the binding Dutch text because his
   *  own language has no translation yet. The page says so rather than
   *  pretending the translation exists. */
  is_translation: boolean;
  binding_language: string;
  version: string;
  sha256: string;
  markdown: string;
}

export interface CompanyDetails {
  legal_name: string | null;
  address: string | null;
  kvk: string | null;
  vat_id: string | null;
  privacy_email: string | null;
  support_email: string | null;
}

export interface CurrentTerms {
  bundle_version: string;
  documents: Record<string, { version: string; sha256: string }>;
}

export interface TermsStatus {
  bundle_version: string;
  outstanding: string[];
}

export interface SignupInput {
  company: string;
  email: string;
  password: string;
  language: Locale;
  terms_version: string;
  /** The honeypot. Hidden in the form, empty for a human. */
  website: string;
  turnstile_token?: string;
  /** Only present when the visitor ticked the ad-attribution box, and then it
   *  always carries `consent: true` -- the server ignores the object without it. */
  attribution?: SignupAttributionInput;
}

/** `consent` plus whichever ad parameters stood in the URL. See lib/adAttribution.ts. */
export type SignupAttributionInput = { consent: true } & AdAttributionParams;

export interface ProfileInput {
  name: string;
  country: 'NL' | 'BE' | 'DE' | 'FR' | 'PL' | 'GB' | 'IE' | 'other';
  language: Locale;
  client_type: ClientType;
}

export interface Profile {
  name: string;
  language: Locale;
  client_type: ClientType;
  home_country: string;
  /** False while the first screen after the first login has not been through. */
  complete: boolean;
}

/** What the "Meet nu" button is doing right now, and what the last run found. */
export type MeasureNowState = {
  state: 'idle' | 'running' | 'done';
  started_at?: string;
  finished_at?: string;
  /** Prices collected by the last finished run. */
  prices?: number;
};

/** Which of the getting-started steps a customer has completed. */
export type SetupState = {
  products: boolean;
  retailers: boolean;
  mappings: boolean;
  /** The two numbers behind `mappings`. One linked product used to tick the
   *  step, so a customer with 12 of 140 linked read "done" and stopped
   *  looking. */
  products_mapped: number;
  products_total: number;
  /** Has a price round ever finished? The fifth step is "Meet nu", and a
   *  customer whose prices landed overnight has it behind him without ever
   *  having pressed anything. */
  measured: boolean;
  own_shop: boolean;
  /** Only an e-tailer sells the products himself, so only an e-tailer has an
   *  own shop to add. This used to be demanded of everybody, and the dashboard
   *  refused to render until it was done -- so a brand without a webshop never
   *  reached the prices he was paying for. */
  own_shop_required: boolean;
  webshop_connected: boolean;
  shop_handle: string | null;
  /** Are the company details complete? They belong on the processing agreement
   *  the customer accepted at signup, and they save him typing the same
   *  address again at checkout. */
  company_details: boolean;
};

/** GET/PUT /api/v1/me/marketplace-accounts (spec B6). */
export interface MarketplaceAccountsState {
  /** His own seller id per marketplace slug. */
  accounts: Record<string, string>;
  /** The name that id carries on his own mapped pages; absent when unknown. */
  names: Record<string, string>;
}

export interface ScanSchedule {
  scans_per_day: number;
  /** Hours are UTC — the jobs run on UTC and storing a local hour would move
   *  the scan twice a year. The screen converts for display. */
  first_scan_hour: number;
  second_scan_hour: number;
  /** False on a plan that buys one scan a day: a control that silently does
   *  nothing is worse than no control. */
  second_scan_configurable: boolean;
  /** Automatic collection is switched off platform-wide. The screen described
   *  a schedule that does not run while this was true. */
  paused: boolean;
}

export interface PlanOption {
  name: PlanName;
  /** Euro per month excl. VAT; null means "on request". */
  price_eur_month: number | null;
  skus: number;
  retailers: number;
  users: number;
  scans_per_day: number;
  price_proposals: boolean;
  dynamic_pricing: boolean;
}

export type PlanName = 'start' | 'business' | 'professional' | 'enterprise';

/** What a customer is on right now. 'trial' is the free trial (200 products);
 *  it can be current but never bought, so it stays out of PlanName. */
export type CurrentPlan = PlanName | 'trial';

/** The three reports. Keys match backend/exports/reports.py — the endpoint
 *  answers 404 with the list of valid ones, so a typo fails loudly. */
/** A saved view is this page's query string under a name — the backend never
 *  reads inside filter_json, so a page that grows a control gets saved views
 *  for it without a migration. */
export interface ConnectStatus {
  enabled: boolean;
  /** The address a customer pastes into Claude/ChatGPT while PriceQ is not yet in their directory. */
  mcp_url: string;
  scopes: string[];
}

/** What an AI app may do (spec 2026-11-03 §3). */
export type AiLevel = 'allow' | 'approval' | 'blocked';

export interface AiPermissionGroup {
  group: string;
  default: AiLevel;
  needs_warning: boolean;
  tools: { name: string; title: string; min_role: string }[];
}

export interface AiPermissions {
  /** `group:<g>` / `tool:<name>` → the level the administrator chose. */
  levels: Record<string, AiLevel>;
  groups: AiPermissionGroup[];
  warning_version: string;
  can_edit: boolean;
}

/** One row of the price effect: old → new per product. */
export interface AiEffectRow {
  sku_id?: string;
  name?: string;
  country?: string;
  current_cents?: number | null;
  new_cents?: number | null;
}

export interface AiPendingAction {
  id: string;
  tool: string;
  summary: string;
  client_name: string;
  requested_by: string;
  requested_by_email: string | null;
  /** `running`: approved and being carried out (stays if it stopped half-way). */
  status: 'pending' | 'running' | 'approved' | 'rejected' | 'expired' | 'failed';
  effect: AiEffectRow[] | null;
  effect_hash: string | null;
  confirm_hint: string | null;
  error: string | null;
  created_at: string;
  expires_at: string;
  decided_by: string | null;
  decided_at: string | null;
}

export interface ConnectGrant {
  id: string;
  client_name: string;
  user_email: string;
  scopes: string[];
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export interface ConnectApproveIn {
  client_id: string;
  redirect_uri: string;
  code_challenge: string;
  code_challenge_method: 'S256';
  scope: string | null;
  state: string | null;
  resource: string | null;
  decision: 'allow' | 'deny';
}

export interface ApiKey {
  id: string;
  name: string;
  /** First characters of the key, in clear — what tells two keys apart in a
   *  list without us ever being able to show the key itself. */
  prefix: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export interface ApiKeyCreated extends ApiKey {
  key: string;
}

export interface SavedView {
  id: string;
  scope: string;
  name: string;
  filter_json: { query?: string };
  created_at: string;
}

export type ReportType = 'prijspositie' | 'adviesprijs' | 'categorie';

/** GET /api/v1/billing/status — mirrors backend/billing/schemas.py:BillingStatus. */
export interface BillingStatus {
  /** "none" means no subscription row at all: a trial, or a customer from
   *  before billing existed. Both keep working. */
  /** `cancelling` is van ons, niet van Stripe: daar is het een gewoon actief
   *  abonnement met cancel_at_period_end -- precies waarom een opzegging
   *  hiervoor onzichtbaar was voor de klant die hem aanvroeg. */
  state: 'none' | 'pending' | 'active' | 'cancelling' | 'past_due' | 'cancelled';
  plan: string | null;
  interval: string | null;
  current_period_end: string | null;
  /** Wanneer een ingeplande opzegging ingaat. Tot die datum verandert er niets. */
  cancel_at: string | null;
  /** Stripe's eigen pagina waar de klant een verlenging moet bevestigen (SCA).
   *  Alleen gevuld zolang er echt iets openstaat. */
  action_required_url: string | null;
  /** Live uit Stripe. Alle vier mogen null zijn als de betaalpartij even wegvalt. */
  trialing_until: string | null;
  next_amount_cents: number | null;
  /** ISO-code van `next_amount_cents` en van de betaalregels. Komt van de
   *  betaaldienst; leeg als die even wegvalt. Nooit aannemen dat het euro's
   *  zijn -- een scherm dat dat vastlegt gaat stil fout bij de eerste klant
   *  die ergens anders in afrekent. */
  currency: string | null;
  next_at: string | null;
  payment_method: string | null;
  trial_ends_at: string | null;
  payments: PaymentLine[];
}

/** GET /api/v1/admin/tenants/{id}/billing — wat de klant zelf ook ziet,
 *  plus de twee velden die alleen een medewerker nodig heeft. Erft van
 *  `BillingStatus` zodat een veld dat daar bij komt hier vanzelf meekomt. */
export interface AdminBillingStatus extends BillingStatus {
  /** Er staat geld open. Een los veld en geen afleiding op het scherm: dit is
   *  het enige op dit blok waar meteen iets mee moet. */
  has_open_invoice: boolean;
  /** Diepe link naar deze klant bij de betaaldienst, voor alles wat dit
   *  scherm niet toont. Leeg bij een klant die nooit heeft afgerekend. */
  provider_customer_url: string | null;
}

export type BillingInterval = 'month' | 'year';

/** POST .../billing/plan/preview — backend/admin/billing_write_api.py:PlanPreview. */
export interface AdminPlanPreview {
  amount_cents: number;
  currency: string;
  /** Whole seconds since 1970: the instant this figure was priced against.
   *  Goes back to the confirm untouched. */
  proration_date: number;
}

/** What the three write actions answer — the provider's state, not ours.
 *  `pending` is always true: our own tables are written by the webhook, so the
 *  screen says the figures follow when the provider confirms. */
export interface AdminSubscriptionState {
  status: string | null;
  cancel_at_period_end: boolean;
  current_period_end: number | null;
  pending: boolean;
}

export interface PaymentLine {
  reference: string;
  kind: string;
  amount_cents: number;
  status: string;
  paid_at: string | null;
  /** Of er een document te openen valt. Het adres zelf komt per klik op, want
   *  het verloopt -- zie api.billing.invoice. */
  has_invoice: boolean;
}

export interface TenantResponse {
  id: string;
  slug: string;
  name: string;
  client_type: ClientType;
  status: TenantStatus;
  // Per-tenant home market (region-aware portal). `home_country` is the source
  // of truth; `currency` / `vat_rate` / `number_locale` are derived server-side
  // from it (mirror of backend market_config.py). Single market per tenant, no FX.
  home_country: string;
  currency: string;
  vat_rate: number;
  number_locale: string;
  created_at: string;
  updated_at: string;
  // Null unless an admin asked for the account to be deleted. Thirty days
  // after this moment the nightly sweep erases everything; until then the
  // request can be withdrawn. See backend/tenants/self_deletion.py.
  deletion_requested_at: string | null;
  // The customer's own company details. Null for every account created before
  // they were asked for; the setup checklist is where they get filled in.
  legal_name: string | null;
  address_line1: string | null;
  postal_code: string | null;
  city: string | null;
  billing_country: string | null;
}

/** The company-details half of a tenant PATCH — the CUSTOMER's own name and
 *  invoice address. Not to be confused with `CompanyDetails` above, which is
 *  Outflanq's own details on the legal documents. Every field optional: an
 *  absent key leaves the column alone server-side. */
export type TenantCompanyDetails = {
  legal_name?: string;
  address_line1?: string;
  postal_code?: string;
  city?: string;
  billing_country?: string;
};

export interface AuditLogItem {
  id: string;
  actor_user_id: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  created_at: string;
}

export interface AuditLogListResponse {
  items: AuditLogItem[];
  total: number;
}

export interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: Role;
  is_active: boolean;
  is_pending: boolean;
  group_id: string | null;
  /** The product groups this member looks after; empty = all. Optional so
   *  the existing test doubles of this response stay valid. */
  category_ids?: string[];
}

export type AlertChannel = 'in_app' | 'email_direct' | 'email_digest';

export interface AlertSetting {
  alert_type: AlertType;
  enabled: boolean;
  channel: AlertChannel;
  threshold_pct: number | null;
  threshold_days: number | null;
  threshold_count: number | null;
  /** null = the shop-wide rule for this alert type. A category makes this row
   *  an exception that governs that category only. */
  category_id: string | null;
  category_name: string | null;
}

export interface AlertSettingsResponse {
  items: AlertSetting[];
}

export interface AlertSettingUpdate {
  enabled?: boolean | null;
  channel?: AlertChannel | null;
  threshold_pct?: number | null;
  threshold_days?: number | null;
  threshold_count?: number | null;
  /** Omitted writes the shop-wide rule; set writes that category's exception. */
  category_id?: string | null;
}

export interface Category {
  id: string;
  slug: string;
  name: string;
  color: string | null;
  sort_order: number;
  /** 'manual' for operator-created; 'woocommerce' / 'shopify' for connector-
   *  owned (read-only — PATCH/DELETE/reorder return 409). */
  source: CategorySource;
  parent_id: string | null;
  external_id: string | null;
  display_name: string | null;
  created_at: string;
  updated_at: string;
}

/** Rail row from GET /categories?include_counts=true. `id` is null for the
 *  virtual `__uncategorized__` row (only emitted when there are orphan SKUs). */
export interface CategoryRailItem {
  id: string | null;
  slug: string;
  name: string;
  color: string | null;
  sort_order: number;
  source: CategorySource;
  parent_id: string | null;
  external_id: string | null;
  display_name: string | null;
  sku_count: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface CategoryListResponse {
  items: Category[];
  total: number;
}

export interface CategoryRailResponse {
  items: CategoryRailItem[];
  /** Excludes the virtual __uncategorized__ row. */
  total: number;
}

export interface CategoryCreate {
  slug: string;
  name: string;
  color?: string | null;
  sort_order?: number;
}

export interface CategoryUpdate {
  name?: string | null;
  color?: string | null;
  sort_order?: number | null;
}

export interface PricePoint {
  scraped_at: string;
  price_cents: number;
}

export interface RetailerSeries {
  monitored_retailer_id: string;
  retailer_display_name: string;
  retailer_slug: string;
  /** The customer's own store — lets the chart accent "your" line. */
  is_self: boolean;
  points: PricePoint[];
}

export interface ReferencePrice {
  country: string;
  sell_price_cents: number | null;
  msrp_cents: number | null;
}

export interface SkuTrendResponse {
  sku_id: string;
  sku_code: string;
  sku_name: string;
  window_days: number;
  series: RetailerSeries[];
  reference_prices: ReferencePrice[];
}

// === Overview types (ADR 0008) ============================================

export type StockStatus = 'in_stock' | 'low_stock' | 'preorder' | 'out_of_stock' | 'unknown';

export interface OverviewRetailer {
  id: string;
  slug: string;
  display_name: string;
  country: string | null;
  is_self?: boolean;
  is_marketplace?: boolean;
}

export interface OverviewCell {
  retailer_id: string;
  listing_id: string;
  price_cents: number;
  currency: string;
  /** null for non-euro (SE/NO) cells — shown but excluded from the EUR rank. */
  rank: number | null;
  group_size: number;
  stock_status: StockStatus;
  /** Set when the stock comes from an older measurement than the price. */
  stock_seen_at?: string | null;
  last_seen: string;
  is_stale: boolean;
  // Marketplace cells only (see OverviewRetailer.is_marketplace):
  seller_name?: string | null;
  is_buybox?: boolean | null;
  offer_count?: number | null;
  // Module customers only — the cheapest counted offer, shipping included.
  lowest_cents?: number | null;
  lowest_seller_name?: string | null;
  /** Marketplace cells: the shown price is his own offer (spec B6). */
  is_own?: boolean | null;
  /** Marketplace cells: the price this cell competes at (plan 3b) -- with
   *  is_own, the cheapest qualifying offer of someone else on that
   *  measurement; null when only his own offer was measured. */
  competitor_cents?: number | null;
}

export interface SellerIdentity {
  business_name: string | null;
  business_type: string | null;
  trade_register: string | null;
  vat_number: string | null;
  country_code: string | null;
  rating_pct: number | null;
}

export interface MarketplaceOffer {
  seller_key: string;
  seller_name: string;
  price_cents: number;
  shipping_cents: number | null;
  landed_cents: number;
  currency: string;
  condition: string;
  in_stock: boolean;
  delivery_from: string | null;
  delivery_to: string | null;
  delivery_text: string | null;
  is_buybox: boolean;
  fulfilled_by_marketplace: boolean | null;
  counts: boolean;
  not_counted_reason: 'not_new' | 'out_of_stock' | 'preorder' | 'no_price' | null;
  is_representative: boolean;
  is_own: boolean;
  /** none: looked up, and the marketplace shows no business details.
   *  own: his own offer (spec B6) -- "jij". */
  identity_state: 'known' | 'pending' | 'none' | 'marketplace' | 'unavailable' | 'own';
  identity: SellerIdentity | null;
}

export interface MarketplaceSellersResponse {
  locked: boolean;
  offer_count: number;
  measured_at: string | null;
  offers: MarketplaceOffer[];
  partial: boolean;
}

export interface OverviewRow {
  sku_id: string;
  sku: string;
  name: string;
  brand: string | null;
  category: string;
  group_key: string | null;
  attributes: Record<string, unknown>;
  cells: OverviewCell[];
  list_price_cents: number | null;
  list_currency: string | null;
  pinned: boolean;
}

export interface OverviewResponse {
  category: string;
  key_attributes: string[];
  available_brands: string[];
  selected_brand: string | null;
  retailers: OverviewRetailer[];
  rows: OverviewRow[];
}

export interface OverviewSparklinesResponse {
  days: number;
  series: Record<string, number[]>;
}

export interface LeaderboardEntry {
  retailer: OverviewRetailer;
  shared_skus: number;
  undercut_count: number;
  undercut_pct: number;
  median_gap_pct: number;
  cheapest_count: number;
}

export interface LeaderboardResponse {
  category: string;
  selected_brand: string | null;
  total_skus: number;
  self_retailer: OverviewRetailer | null;
  entries: LeaderboardEntry[];
}

export interface CellSkuSummary {
  sku_id: string;
  sku: string;
  name: string;
  brand: string | null;
  category: string;
  group_key: string | null;
  attributes: Record<string, unknown>;
  // Reference prices for the retailer's country — drives the "vs list" delta
  // in ListingDetailsCard. Null when the tenant hasn't seeded MSRP/sell.
  msrp_cents: number | null;
  sell_price_cents: number | null;
  reference_currency: string | null;
}

export interface CellListingTab {
  listing_id: string;
  title: string;
  listing_url: string;
  ean: string | null;
  mpn: string | null;
  price_cents: number;
  currency: string;
  stock_status: StockStatus;
  /** Set when the stock comes from an older measurement than the price. */
  stock_seen_at?: string | null;
  last_seen: string;
  match_source: string;
  match_kind: string;
  match_confidence: number | null;
}

export interface CellPeer {
  retailer_id: string;
  retailer_slug: string;
  retailer_display_name: string;
  is_self: boolean;
  rank: number;
  listing_id: string | null;
  listing_url: string;
  price_cents: number;
  currency: string;
  stock_status: StockStatus;
  /** Set when the stock comes from an older measurement than the price. */
  stock_seen_at?: string | null;
  last_seen: string;
  // Delta vs the current cell's price: negative = this peer is cheaper.
  delta_cents: number;
  delta_pct: number;
  is_cheaper: boolean;
}

export interface CellPeerStats {
  count: number;
  avg_price_cents: number;
  median_price_cents: number;
  min_price_cents: number;
  max_price_cents: number;
}

export interface CellOtherSelfSku {
  sku_id: string;
  sku: string;
  name: string;
  listing_id: string;
  price_cents: number;
}

export interface CellPeersTab {
  group_key: string | null;
  stats: CellPeerStats | null;
  peers: CellPeer[];
  other_self_skus_in_group: CellOtherSelfSku[];
}

export interface CellHistoryPoint {
  scraped_at: string;
  price_cents: number;
}

export interface CellHistoryEvent {
  date: string;
  kind: 'first_seen' | 'price_drop' | 'price_rise';
  delta_pct: number | null;
  from_cents: number | null;
  to_cents: number | null;
}

/** One shop's price line over the window. */
export interface CellHistorySeries {
  retailer_id: string;
  retailer_slug: string;
  display_name: string;
  is_self: boolean;
  points: CellHistoryPoint[];
}

export interface CellHistoryTab {
  window_days: number;
  points: CellHistoryPoint[];
  events: CellHistoryEvent[];
  /** Every mapped shop, self first. `points` stays the tenant's own listing. */
  series: CellHistorySeries[];
}

/**
 * Customer-facing "why is this matched" block. No tier scores, credits or
 * tokens — those are matching plumbing and never surface in the UI.
 */
export interface CellMatchTab {
  method: string | null;
  method_label: string;
  confidence: number | null;
  confidence_label: string;
  verified: boolean;
  matched_at: string | null;
}

export interface OverviewCellDetailResponse {
  sku: CellSkuSummary;
  retailer: OverviewRetailer;
  tab_listing: CellListingTab;
  tab_peers: CellPeersTab;
  tab_history: CellHistoryTab;
  tab_match: CellMatchTab;
}

export type AlertType =
  | 'competitor_cheaper'
  | 'structurally_more_expensive'
  | 'sudden_price_move'
  | 'new_retailer'
  | 'retailer_dropped_product'
  | 'multi_retailer_oos'
  | 'own_price_inconsistent'
  // E-tailer detectors (added 2026-05-27, migration 0088).
  | 'own_undercut'
  | 'own_oos_competitor_in'
  | 'new_seller'
  | 'buybox_changed'
  // 0052 enum-value (currently no detector emitting it, kept for type-narrow).
  | 'back_in_stock';

export type AlertStatus = 'unread' | 'read' | 'snoozed' | 'ignored' | 'muted' | 'resolved';
export type AlertSeverity = 'info' | 'warning' | 'critical';

export interface SkuCategoryTag {
  slug: string;
  display_name: string;
}

export interface Alert {
  id: string;
  alert_type: AlertType;
  severity: AlertSeverity;
  status: AlertStatus;
  title: string;
  body: string;
  payload: Record<string, unknown>;
  sku_id: string | null;
  // Populated by list_alerts via JOIN with skus + product_categories.
  // Null when the alert has no sku_id (rare). Optional so older tests +
  // mock fixtures stay compatible without backfilling the field.
  sku_category?: SkuCategoryTag | null;
  monitored_retailer_id: string | null;
  snooze_until: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  // Event-aware fields (own_undercut / own_oos_competitor_in). Lifted from
  // payload by the backend; null for legacy alerts + non-e-tailer types.
  impact_score?: number | null;
  event_status?: 'new' | 'worsened' | 'unchanged' | null;
  first_detected_at?: string | null;
}

export interface AlertListResponse {
  items: Alert[];
  total: number;
  unread_count: number;
}

/** One own-shop active promotion (derived: connector sell_price < msrp). */
export interface ActivePromoItem {
  sku_id: string;
  sku: string;
  name: string;
  country: string;
  promo_cents: number;
  list_cents: number;
  depth_pct: number;
  ends_at: string | null;
}

export interface AlertSummary {
  total_open: number;
  by_type: Record<string, number>;
  by_severity: Record<string, number>;
}

/** A sale the customer is running himself, derived from connector data
 *  (sell price below his own list price). Shown on Alerts as a calm strip so
 *  he can see exactly what PriceQ compares against and keeps quiet about. */
export interface ActivePromoItem {
  sku_id: string;
  sku: string;
  name: string;
  country: string;
  promo_cents: number;
  list_cents: number;
  depth_pct: number;
  ends_at: string | null;
}

export interface ActivePromosResponse {
  items: ActivePromoItem[];
}

export interface GenerateAlertsResponse {
  scanned_listings: number;
  new_alerts: number;
  re_emitted_skipped: number;
  by_type: Record<string, number>;
}

// MatchQueueItem — DEPRECATED in v2; kept as type-alias because some
// places still reference the shape. Backend match.queue() returns empty.
export interface MatchQueueItem {
  listing_id: string;
  listing_url: string;
  title: string;
  ean: string | null;
  price_cents: number;
  currency: string;
  retailer_slug: string;
  retailer_display_name: string;
  confidence: number;
  decided_by: string;
  scraped_at: string;
}

export interface MatchQueueListResponse {
  items: MatchQueueItem[];
  total: number;
}

// === V2 mapping types (Fase 6) ============================================

// Canonical sku_url_mappings FSM (ADR-0025). NOT the channel FSM
// (active/degraded/dead) — see .ai/redesign/sources/contract.md §(a) drift-fix.
export type MappingState =
  | 'unmatched'
  | 'in_review'
  | 'matched'
  | 'never_listed'
  | 'dismissed';

export interface MappingSkuSummary {
  id: string;
  sku: string;
  name: string;
  brand: string | null;
  ean: string;
  category: string | null;
}

export interface MappingRetailerSummary {
  id: string;
  slug: string;
  display_name: string;
  country: string;
}

export interface Mapping {
  id: string;
  sku_id: string;
  monitored_retailer_id: string;
  listing_url: string;
  normalized_url: string;
  state: MappingState;
  confidence: number | null; // 0..100 or -1 (ean_mismatch)
  bucket: string | null; // green / amber / red
  flags: string[];
  dismissed_at: string | null;
  created_at: string;
  updated_at: string;
  // JOINed by GET /mappings so the Koppelingen-tab renders without a 2nd
  // fetch (contract.md gap A3). Null on single-row endpoints that don't JOIN.
  sku?: MappingSkuSummary | null;
  retailer?: MappingRetailerSummary | null;
  last_observed_at?: string | null;
  // Set when the page has shown no price for 30+ days: the day the last price
  // was seen (or the link was made). Only the list endpoint fills it.
  unavailable_since?: string | null;
}

export interface MappingListResponse {
  items: Mapping[];
  total: number;
}

export interface UrlImportError {
  row: number;
  reason: string;
}

export interface UrlImportResponse {
  bulk_run_id: string | null;
  total_rows: number;
  accepted_rows: number;
  duplicate_rows: number;
  errors: UrlImportError[];
}

export interface BulkRunStatus {
  bulk_run_id: string;
  trigger_type: string;
  started_at: string | null;
  completed_at: string | null;
  total_jobs: number;
  by_state: Record<string, number>;
  completed_pct: number;
  eta_seconds: number | null;
  failed_jobs: Array<{ sku_id: string; retailer_id: string; last_error: string | null }>;
}

export interface ManualMappingRequest {
  sku_id: string;
  monitored_retailer_id: string;
  listing_url: string;
  skip_ean_validation?: boolean;
}

export interface TriggerMappingJobRequest {
  sku_ids?: string[];
  monitored_retailer_ids?: string[];
}

export interface TriggerMappingJobResponse {
  enqueued_jobs: number;
  skipped_existing: number;
}

export interface TierBreakdown {
  name: string;
  matched: boolean;
  weight: number;
  value: number;
  note: string | null;
}

export interface CandidateScore {
  final: number;        // -1..100; -1 = ean_mismatch
  bucket: 'green' | 'amber' | 'red';
  flags: string[];
  tiers: Record<string, TierBreakdown>;
}

export interface ReviewCandidate {
  listing_url: string;
  pdp_ean: string | null;
  pdp_mpn: string | null;
  matched: boolean;
  reason: string | null;
  // ADR-0026 — score-breakdown badges:
  pdp_brand?: string | null;
  score?: CandidateScore | null;
}

export interface ReasonCode {
  code: string;
  detail_nl: string | null;
  weight: number;
}

export interface ReviewItemDetails {
  reason?: string;
  candidates?: ReviewCandidate[];
  /** The page the matcher found and refused. It was in the payload all
   *  along and shown nowhere, so the customer got an empty paste box and
   *  had to go looking for what we already had in hand. */
  listing_url?: string | null;
  primary_reason_code?: string | null;
  primary_reason_text_nl?: string | null;
  reasoning?: ReasonCode[];
}

export interface ReviewSkuSummary {
  id: string;
  sku: string;
  name: string;
  ean: string;
  brand: string | null;
  category: string | null;
}

export interface ReviewRetailerSummary {
  id: string;
  slug: string;
  display_name: string;
  country: string;
}

export interface ReviewItem {
  id: string;
  sku_id: string;
  monitored_retailer_id: string;
  sku?: ReviewSkuSummary | null;
  retailer?: ReviewRetailerSummary | null;
  state: 'queued' | 'running' | 'mapped' | 'needs_review' | 'abandoned';
  candidates_examined: number;
  details: ReviewItemDetails | null;
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface ReviewQueueResponse {
  items: ReviewItem[];
  total: number;
}

export interface ResolveReviewRequest {
  action: 'accept_url' | 'abandon';
  listing_url?: string;
  skip_ean_validation?: boolean;
}

export { ApiError };
